// src/features/sales/pages/contactPage/ContactsPage.jsx

import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import api from "../../../../shared/api/apiClient";
import { useLocation, useNavigate } from "react-router-dom";
import TelephonyModal from "../../../../shared/components/telephony/TelephonyModal";
import { getUserPermissions } from "../../../../shared/lib/auth/authHelper";

import { emailApi } from "../../api/emailApi";
import { aiApi } from "../../api/aiApi";

import HtmlEditorWithPreview from "../../../../shared/components/email/HtmlEditorWithPreview";
import BulkEmailModal from "../../../../shared/components/email/BulkEmailModal";
import MarketingContactPanel from "../../../marketing/components/MarketingContactPanel";
import "./ContactsPage.css";

const getApiErrorText = (error, fallback = "") => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(", ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return error?.message || fallback;
};

const CONTACTS_RETRY_DELAYS_MS = [1200, 3500, 8000];

const isTransientContactsError = (error) => {
  const status = Number(error?.response?.status || 0);
  return !status || status === 408 || status === 425 || status === 429 || status >= 500;
};

const getContactsRetryDelay = (error, attempt) => {
  const retryAfter = error?.response?.headers?.["retry-after"];
  if (retryAfter) {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds)) {
      return Math.min(Math.max(seconds * 1000, 1000), 60000);
    }

    const retryAt = Date.parse(retryAfter);
    if (Number.isFinite(retryAt)) {
      return Math.min(Math.max(retryAt - Date.now(), 1000), 60000);
    }
  }

  return CONTACTS_RETRY_DELAYS_MS[
    Math.min(attempt, CONTACTS_RETRY_DELAYS_MS.length - 1)
  ];
};

const normalizeTelegramUsername = (value) =>
  String(value || "")
    .trim()
    .replace(/^https?:\/\/(?:www\.)?t\.me\//i, "")
    .replace(/^t\.me\//i, "")
    .replace(/^@+/, "")
    .split(/[/?#]/, 1)[0]
    .trim();

const formatTelegramUsername = (value) => {
  const username = normalizeTelegramUsername(value);
  return username ? `@${username}` : "--";
};

const isPastDate = (value) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
};

const isCampaignAvailableForContacts = (campaign) => {
  const status = campaign?.status;
  if (["expired", "pending_delete", "cancelled", "completed"].includes(status)) return false;
  if (isPastDate(campaign?.ends_at)) return false;
  return true;
};


const getCompanyLabel = (company) => {
  if (!company) return "";
  return (
    company.name ||
    company.title ||
    company.company_name ||
    company.full_name ||
    company.email ||
    `Company #${company.ui_id || company.id}`
  );
};

const getCompanyUiId = (company) => company?.ui_id || company?.id;

const companyMatchesSearch = (company, search) => {
  const q = String(search || "").trim().toLowerCase();
  if (!q) return true;
  return [company?.id, company?.ui_id, getCompanyLabel(company), company?.email, company?.phone]
    .map((v) => (v === null || v === undefined ? "" : String(v).toLowerCase()))
    .some((v) => v.includes(q));
};

const getCompactPaginationItems = (currentPage, totalPages) => {
  const pages = [];
  const add = (item) => {
    if (!pages.includes(item)) pages.push(item);
  };

  if (!totalPages || totalPages <= 1) return [1];

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i += 1) add(i);
    return pages;
  }

  add(1);

  if (currentPage > 4) add("left-ellipsis");

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i += 1) add(i);

  if (currentPage < totalPages - 3) add("right-ellipsis");

  add(totalPages);
  return pages;
};


const CONTACT_SYSTEM_COLUMNS = [
  { key: "ui_id", label: "id", defaultVisible: true },
  { key: "full_name", label: "sales_contacts_name", defaultVisible: true },
  { key: "email", label: "sales_contacts_email", defaultVisible: true },
  { key: "phone", label: "sales_contacts_phone", defaultVisible: true },
  { key: "telegram", label: "marketing_channel_telegram_name", defaultVisible: true },
  { key: "contact_status", label: "contact_status", defaultVisible: true },
  { key: "company_id", label: "sales_contacts_company", defaultVisible: true },
  { key: "owner_id", label: "sales_contacts_owner", defaultVisible: true },
  { key: "created_at", label: "created_at", defaultVisible: true },
];

function ContactsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const academyTableParams = new URLSearchParams(location.search);
  const academyTablesActive =
    academyTableParams.get("academy_tables") === "1";
  const academyContactQuery =
    academyTableParams.get("academy_contact") || "";
  const academyContactQueryRef = useRef(academyContactQuery);
  const contactsRequestRef = useRef(0);
  const contactsAbortRef = useRef(null);
  const contactsRetryTimerRef = useRef(null);
  const contactsRetryAttemptRef = useRef(0);
  const contactsSearchTimerRef = useRef(null);
  const canUpdateContacts = getUserPermissions()?.includes("contacts.update");

  const [contacts, setContacts] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [contactsPerPage, setContactsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);

  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnSettingsLoading, setColumnSettingsLoading] = useState(false);
  const [columnSettingsSaving, setColumnSettingsSaving] = useState(false);
  const [columnSettingsError, setColumnSettingsError] = useState("");
  const columnSettingsRef = useRef(null);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsModalType, setDetailsModalType] = useState("email");
  const [detailsContact, setDetailsContact] = useState(null);
  const [detailsItems, setDetailsItems] = useState([]);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createEmailError, setCreateEmailError] = useState("");
  const [createForm, setCreateForm] = useState({
    full_name: "",
    company_id: "",
    owner_id: "",
    email: "",
    phone: "",
    custom_field_values: {},
  });

  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState("");

  const companyEditorRef = useRef(null);
  const companyDropdownRef = useRef(null);
  const [editingCompanyContactId, setEditingCompanyContactId] = useState(null);
  const [companySearch, setCompanySearch] = useState("");
  const [savingCompanyContactId, setSavingCompanyContactId] = useState(null);
  const [companyDropdownPosition, setCompanyDropdownPosition] = useState(null);

  const [inlineContactEditor, setInlineContactEditor] = useState(null);
  const [inlineContactDraft, setInlineContactDraft] = useState("");
  const [inlineContactSaving, setInlineContactSaving] = useState(false);
  const [inlineContactError, setInlineContactError] = useState("");

  const [expandedEmailRowId, setExpandedEmailRowId] = useState(null);
  const [expandedPhoneRowId, setExpandedPhoneRowId] = useState(null);

  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [sendEmailContact, setSendEmailContact] = useState(null);
  const [sendEmailForm, setSendEmailForm] = useState({
    to: "",
    subject: "",
    template: "no_template",
    html_body: "",
    sender: "auto",
  });
  const [sendEmailErrors, setSendEmailErrors] = useState({});
  const [sendEmailSending, setSendEmailSending] = useState(false);
  const [sendEmailDraftLoading, setSendEmailDraftLoading] = useState(false);
  const [sendEmailAiTone, setSendEmailAiTone] = useState("professional");
  const [sendEmailAiObjective, setSendEmailAiObjective] = useState("follow_up");
  const [allowSenderOverrideSingle, setAllowSenderOverrideSingle] =
    useState(false);

  const [bulkEmailModalOpen, setBulkEmailModalOpen] = useState(false);
  const [bulkEmailForm, setBulkEmailForm] = useState({
    to_preview: "",
    subject: "",
    template: "no_template",
    html_body: "",
    tag: "",
    is_test: false,
    sender: "auto",
  });
  const [bulkEmailErrors, setBulkEmailErrors] = useState({});
  const [bulkEmailSending, setBulkEmailSending] = useState(false);
  const [bulkRecipients, setBulkRecipients] = useState([]);

  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadModalContact, setLeadModalContact] = useState(null);

  const [marketingModalOpen, setMarketingModalOpen] = useState(false);
  const [marketingContact, setMarketingContact] = useState(null);

  const [bulkMarketingModalOpen, setBulkMarketingModalOpen] = useState(false);
  const [bulkMarketingCampaigns, setBulkMarketingCampaigns] = useState([]);
  const [bulkMarketingCampaignSearch, setBulkMarketingCampaignSearch] = useState("");
  const [bulkMarketingLoading, setBulkMarketingLoading] = useState(false);
  const [bulkMarketingSending, setBulkMarketingSending] = useState(false);
  const [bulkMarketingError, setBulkMarketingError] = useState("");
  const [bulkMarketingForm, setBulkMarketingForm] = useState({
    campaign_id: "",
    send_mode: "now",
    scheduled_at: "",
  });
  const [leadForm, setLeadForm] = useState({
    title: "",
    source_id: "",
    budget: "",
    description: "",
  });
  const [leadSaving, setLeadSaving] = useState(false);
  const [leadError, setLeadError] = useState("");

  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState("");

  const openMarketingModal = (contact) => {
    setMarketingContact(contact);
    setMarketingModalOpen(true);
  };

  const closeMarketingModal = () => {
    setMarketingModalOpen(false);
    setMarketingContact(null);
  };

  const loadBulkMarketingCampaigns = async () => {
    try {
      setBulkMarketingLoading(true);
      setBulkMarketingError("");
      const { data } = await api.get("/marketing/campaigns", {
        params: { skip: 0, limit: 100 },
      });
      setBulkMarketingCampaigns(
        Array.isArray(data?.items)
          ? data.items.filter(isCampaignAvailableForContacts)
          : []
      );
    } catch (e) {
      console.error(e);
      setBulkMarketingCampaigns([]);
      setBulkMarketingError(getApiErrorText(e, t("contacts_campaigns_load_failed")));
    } finally {
      setBulkMarketingLoading(false);
    }
  };

  const openBulkMarketingModal = async () => {
    if (selectedIds.length === 0) return;
    setBulkMarketingCampaignSearch("");
    setBulkMarketingForm({
      campaign_id: "",
      send_mode: "now",
      scheduled_at: "",
    });
    setBulkMarketingError("");
    setBulkMarketingSending(false);
    setBulkMarketingModalOpen(true);
    await loadBulkMarketingCampaigns();
  };

  const closeBulkMarketingModal = () => {
    setBulkMarketingModalOpen(false);
    setBulkMarketingCampaignSearch("");
    setBulkMarketingError("");
    setBulkMarketingSending(false);
  };

  const handleBulkMarketingFormChange = (field, value) => {
    setBulkMarketingForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "send_mode" && value !== "scheduled" ? { scheduled_at: "" } : {}),
    }));
    setBulkMarketingError("");
  };

  const filteredBulkMarketingCampaigns = bulkMarketingCampaigns.filter((campaign) => {
    const q = bulkMarketingCampaignSearch.trim().toLowerCase();
    if (!q) return true;
    return [campaign.id, campaign.title, campaign.description, campaign.status]
      .map((value) => (value == null ? "" : String(value).toLowerCase()))
      .some((value) => value.includes(q));
  });

  const selectedBulkMarketingCampaign = bulkMarketingCampaigns.find(
    (campaign) => String(campaign.id) === String(bulkMarketingForm.campaign_id)
  );

  const handleBulkMarketingSubmit = async () => {
    if (selectedIds.length === 0) {
      setBulkMarketingError(t("contacts_select_client"));
      return;
    }
    if (!bulkMarketingForm.campaign_id) {
      setBulkMarketingError(t("contacts_select_campaign"));
      return;
    }
    if (bulkMarketingForm.send_mode === "scheduled" && !bulkMarketingForm.scheduled_at) {
      setBulkMarketingError(t("contacts_select_send_date"));
      return;
    }

    try {
      setBulkMarketingSending(true);
      setBulkMarketingError("");

      const payload = {
        contact_ids: selectedIds,
        send_mode: bulkMarketingForm.send_mode,
        scheduled_at:
          bulkMarketingForm.send_mode === "scheduled"
            ? `${bulkMarketingForm.scheduled_at}:00`
            : null,
      };

      const { data } = await api.post(
        `/marketing/campaigns/${bulkMarketingForm.campaign_id}/send-to-contacts`,
        payload
      );

      setSuccess(t("contacts_campaign_queued", {
        email: data?.email_jobs || 0,
        telegram: data?.telegram_jobs || 0,
      }));
      setSelectedIds([]);
      closeBulkMarketingModal();
      await fetchContacts(currentPage, contactsPerPage, searchQuery);
    } catch (e) {
      console.error(e);
      setBulkMarketingError(getApiErrorText(e, t("contacts_campaign_send_failed")));
    } finally {
      setBulkMarketingSending(false);
    }
  };

  const contactStatusClassMap = {
    cold: "bg-secondary",
    lead: "bg-info",
    customer: "bg-success",
    churned: "bg-dark",
  };

  const [callModalOpen, setCallModalOpen] = useState(false);
  const [callContact, setCallContact] = useState(null);
  const [callPhone, setCallPhone] = useState("");
  const [isCalling, setIsCalling] = useState(false);
  const [activeCallSid, setActiveCallSid] = useState("");
  const [callError, setCallError] = useState("");

  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [activeRecordingSid, setActiveRecordingSid] = useState("");

  const [, setTwilioReady] = useState(false);
  const [, setTwilioLoading] = useState(false);
  const [, setTwilioError] = useState("");

  const twilioDeviceRef = useRef(null);
  const activeConnectionRef = useRef(null);

  const [micLevel, setMicLevel] = useState(0);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const animationFrameRef = useRef(null);

  const isColumnVisible = (key, defaultVisible = true) => {
    const configured = columnVisibility?.[key];
    return typeof configured === "boolean" ? configured : defaultVisible;
  };

  const visibleCustomFields = customFields.filter((field) =>
    isColumnVisible(
      `custom:${field.key}`,
      field.is_visible_in_table === true || field.is_visible_in_table === 1
    )
  );

  const contactColumnOptions = [
    ...CONTACT_SYSTEM_COLUMNS.map((column) => ({
      ...column,
      label: t(column.label),
    })),
    ...customFields.map((field) => ({
      key: `custom:${field.key}`,
      label: t(field.label || field.key),
      defaultVisible:
        field.is_visible_in_table === true || field.is_visible_in_table === 1,
      custom: true,
    })),
  ];

  const loadContactColumnSettings = async () => {
    try {
      setColumnSettingsLoading(true);
      setColumnSettingsError("");
      const { data } = await api.get("/auth/crmSettings");
      const savedColumns = data?.settings_json?.contact_table?.columns;
      setColumnVisibility(savedColumns && typeof savedColumns === "object" ? savedColumns : {});
    } catch (e) {
      console.error(e);
      setColumnSettingsError(getApiErrorText(e, t("contacts_column_settings_load_failed")));
    } finally {
      setColumnSettingsLoading(false);
    }
  };

  const saveContactColumnSettings = async (nextVisibility) => {
    try {
      setColumnSettingsSaving(true);
      setColumnSettingsError("");
      await api.put("/auth/crmSettings", {
        settings_json: { contact_table: { columns: nextVisibility } },
      });
    } catch (e) {
      console.error(e);
      setColumnSettingsError(getApiErrorText(e, t("contacts_column_settings_save_failed")));
      throw e;
    } finally {
      setColumnSettingsSaving(false);
    }
  };

  const toggleContactColumn = async (option) => {
    const previousVisibility = columnVisibility;
    const nextVisibility = {
      ...previousVisibility,
      [option.key]: !isColumnVisible(option.key, option.defaultVisible),
    };
    setColumnVisibility(nextVisibility);
    try {
      await saveContactColumnSettings(nextVisibility);
    } catch {
      setColumnVisibility(previousVisibility);
    }
  };

  const resetContactColumns = async () => {
    const nextVisibility = Object.fromEntries(
      contactColumnOptions.map((option) => [option.key, Boolean(option.defaultVisible)])
    );
    const previousVisibility = columnVisibility;
    setColumnVisibility(nextVisibility);
    try {
      await saveContactColumnSettings(nextVisibility);
    } catch {
      setColumnVisibility(previousVisibility);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const { data } = await api.get("/custom-fields/contact");
      setCustomFields(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setCustomFields([]);
    }
  };

  const toggleEmailExpand = (contactId) => {
    setExpandedEmailRowId((prev) => (prev === contactId ? null : contactId));
  };

  const togglePhoneExpand = (contactId) => {
    setExpandedPhoneRowId((prev) => (prev === contactId ? null : contactId));
  };

  const sortMainFirst = (items = []) =>
    [...items].sort((a, b) => {
      if (a.status === "main" && b.status !== "main") return -1;
      if (b.status === "main" && a.status !== "main") return 1;
      return 0;
    });

  const getEmailsString = (c) => {
    if (!c?.emails || c.emails.length === 0) return "";
    return c.emails
      .map((e) => (e.status === "main" ? `${e.email} (main)` : e.email))
      .join(", ");
  };

  const getPhonesString = (c) => {
    if (!c?.phones || c.phones.length === 0) return "";
    return c.phones
      .map((p) => (p.status === "main" ? `${p.phone} (main)` : p.phone))
      .join(", ");
  };

  const getTelegramDisplay = (contact) => {
    const username = normalizeTelegramUsername(contact?.telegram_username);
    if (username) return `@${username}`;
    if (contact?.telegram_user_id) return `ID ${contact.telegram_user_id}`;
    if (contact?.telegram_chat_id) return `Chat ${contact.telegram_chat_id}`;
    return "";
  };

  const openInlineContactEditor = (contact, field) => {
    if (!canUpdateContacts || !contact?.id) return;

    setInlineContactEditor({ contactId: contact.id, field });
    setInlineContactDraft(
      field === "telegram_username"
        ? normalizeTelegramUsername(contact.telegram_username)
        : String(contact.full_name || "")
    );
    setInlineContactError("");
  };

  const closeInlineContactEditor = () => {
    if (inlineContactSaving) return;
    setInlineContactEditor(null);
    setInlineContactDraft("");
    setInlineContactError("");
  };

  const saveInlineContactField = async (contact) => {
    if (!contact?.id || !inlineContactEditor) return;

    const field = inlineContactEditor.field;
    const value =
      field === "telegram_username"
        ? normalizeTelegramUsername(inlineContactDraft)
        : inlineContactDraft.trim();

    if (field === "full_name" && !value) {
      setInlineContactError(
        t("full_name_required", { defaultValue: "Укажите имя контакта." })
      );
      return;
    }

    if (
      field === "telegram_username" &&
      value &&
      !/^[A-Za-z0-9_]{5,32}$/.test(value)
    ) {
      setInlineContactError(
        t("telegram_username_invalid", {
          defaultValue: "Используйте 5–32 латинских символа, цифры или _.",
        })
      );
      return;
    }

    try {
      setInlineContactSaving(true);
      setInlineContactError("");

      const payload = {
        [field]: field === "telegram_username" ? value || null : value,
      };
      await api.put(`/contacts/${contact.id}`, payload);

      const patch = { [field]: payload[field] };
      setContacts((prev) =>
        prev.map((item) =>
          item.id === contact.id ? { ...item, ...patch } : item
        )
      );
      setDetailsContact((prev) =>
        prev?.id === contact.id ? { ...prev, ...patch } : prev
      );
      setInlineContactEditor(null);
      setInlineContactDraft("");
    } catch (e) {
      console.error(e);
      setInlineContactError(
        getApiErrorText(
          e,
          t("error_updating_contact", {
            defaultValue: "Не удалось обновить контакт.",
          })
        )
      );
    } finally {
      setInlineContactSaving(false);
    }
  };

  const renderInlineContactField = (contact, field, compact = false) => {
    const isTelegram = field === "telegram_username";
    const isEditing =
      inlineContactEditor?.contactId === contact.id &&
      inlineContactEditor?.field === field;
    const username = normalizeTelegramUsername(contact.telegram_username);
    const displayValue = isTelegram
      ? formatTelegramUsername(contact.telegram_username)
      : String(contact.full_name || "").trim() || "--";

    if (isEditing) {
      return (
        <div
          className={`contactInlineEditor ${compact ? "isCompact" : ""}`}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="contactInlineControl">
            {isTelegram && (
              <span className="contactInlinePrefix" aria-hidden="true">
                @
              </span>
            )}
            <input
              autoFocus
              type="text"
              value={inlineContactDraft}
              maxLength={isTelegram ? 32 : 255}
              aria-label={isTelegram ? "Telegram" : t("sales_contacts_name")}
              onChange={(event) =>
                setInlineContactDraft(
                  isTelegram
                    ? normalizeTelegramUsername(event.target.value)
                    : event.target.value
                )
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  saveInlineContactField(contact);
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  closeInlineContactEditor();
                }
              }}
            />
          </div>
          <div className="contactInlineActions">
            <button
              type="button"
              className="contactInlineSave"
              disabled={inlineContactSaving}
              onClick={() => saveInlineContactField(contact)}
              title={t("save", { defaultValue: "Сохранить" })}
              aria-label={t("save", { defaultValue: "Сохранить" })}
            >
              {inlineContactSaving ? (
                <span className="contactInlineSpinner" />
              ) : (
                <span aria-hidden="true">✓</span>
              )}
            </button>
            <button
              type="button"
              className="contactInlineCancel"
              disabled={inlineContactSaving}
              onClick={closeInlineContactEditor}
              title={t("cancel", { defaultValue: "Отмена" })}
              aria-label={t("cancel", { defaultValue: "Отмена" })}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          {inlineContactError && (
            <span className="contactInlineError" role="alert">
              {inlineContactError}
            </span>
          )}
        </div>
      );
    }

    if (!canUpdateContacts) {
      return isTelegram && username ? (
        <a
          className="contactInlineReadonly contactInlineTelegramLink"
          href={`https://t.me/${username}`}
          target="_blank"
          rel="noreferrer"
        >
          {displayValue}
        </a>
      ) : (
        <span className="contactInlineReadonly">{displayValue}</span>
      );
    }

    return (
      <div className={`contactInlineField ${compact ? "isCompact" : ""}`}>
        <button
          type="button"
          className={`contactInlineValue ${displayValue === "--" ? "isEmpty" : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            openInlineContactEditor(contact, field);
          }}
          title={t("edit", { defaultValue: "Изменить" })}
        >
          <span>{displayValue}</span>
          <i className="lni lni-pencil" aria-hidden="true" />
        </button>
        {isTelegram && username && (
          <a
            className="contactInlineOpenTelegram"
            href={`https://t.me/${username}`}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            title={t("contacts_open_telegram")}
            aria-label={t("contacts_open_telegram")}
          >
            <i className="lni lni-telegram" />
          </a>
        )}
      </div>
    );
  };

  const entityButtonStyle = {
    border: "none",
    background: "transparent",
    color: "#8fb3ff",
    padding: 0,
    fontWeight: 750,
    textAlign: "left",
    cursor: "pointer",
    lineHeight: 1.16,
    display: "block",
    maxWidth: 220,
    minWidth: 0,
  };

  const entityTitleStyle = {
    display: "block",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const entityMetaStyle = {
    display: "block",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "rgba(255,255,255,0.52)",
    fontSize: 11,
    marginTop: 2,
    fontWeight: 600,
  };

  const renderOwnerCell = (contact) => {
    if (!contact?.owner_id) return t("n_a");

    const label =
      contact.owner_display_name ||
      contact.owner_full_name ||
      contact.owner_email ||
      `${t("sales_contacts_owner", { defaultValue: "Owner" })} #${contact.owner_ui_id || contact.owner_id}`;

    const meta = contact.owner_email || (contact.owner_ui_id ? `ID ${contact.owner_ui_id}` : "");

    return (
      <button
        type="button"
        style={entityButtonStyle}
        title={t("open_employee", { defaultValue: "Open employee" })}
        onClick={() => navigate(`/menu/employees?user_id=${contact.owner_id}`)}
      >
        <span style={entityTitleStyle}>{label}</span>
        {meta && <span style={entityMetaStyle}>{meta}</span>}
      </button>
    );
  };

  const getCustomValue = (contact, field) => {
    const values = contact?.custom_field_values || {};
    let value = values[field.key];

    if (
      (value === null || value === undefined || value === "") &&
      field.default_value !== null &&
      field.default_value !== undefined &&
      field.default_value !== ""
    ) {
      value = field.default_value;
    }

    if (value === true || value === "true") return t("yes");
    if (value === false || value === "false") return t("no");

    if (value === null || value === undefined || value === "") {
      return "-";
    }

    return String(value);
  };

  const clearContactsRetry = () => {
    if (contactsRetryTimerRef.current) {
      window.clearTimeout(contactsRetryTimerRef.current);
      contactsRetryTimerRef.current = null;
    }
  };

  const scheduleContactsRetry = (requestError, page, perPage, search) => {
    if (
      !isTransientContactsError(requestError) ||
      contactsRetryTimerRef.current ||
      contactsRetryAttemptRef.current >= CONTACTS_RETRY_DELAYS_MS.length
    ) {
      return;
    }

    const attempt = contactsRetryAttemptRef.current;
    contactsRetryAttemptRef.current += 1;
    const delay = getContactsRetryDelay(requestError, attempt);

    contactsRetryTimerRef.current = window.setTimeout(() => {
      contactsRetryTimerRef.current = null;
      fetchContacts(page, perPage, search, { backgroundRetry: true });
    }, delay);
  };

  async function fetchContacts(
    page = 1,
    perPage = contactsPerPage,
    search = searchQuery,
    options = {}
  ) {
    const { backgroundRetry = false } = options;

    if (!backgroundRetry) {
      clearContactsRetry();
      contactsRetryAttemptRef.current = 0;
    }

    const requestId = contactsRequestRef.current + 1;
    contactsRequestRef.current = requestId;

    contactsAbortRef.current?.abort();
    const controller = new AbortController();
    contactsAbortRef.current = controller;

    try {
      setLoading(true);
      setError("");
      if (!backgroundRetry) setSuccess("");

      const skip = (page - 1) * perPage;

      const { data } = await api.get("/contacts", {
        signal: controller.signal,
        params: {
          skip,
          limit: perPage,
          q: search || undefined,
        },
      });

      if (requestId !== contactsRequestRef.current) return;

      clearContactsRetry();
      contactsRetryAttemptRef.current = 0;
      setContacts(Array.isArray(data.items) ? data.items : []);
      setTotalContacts(data.total || 0);
      setSelectedIds([]);
      setCurrentPage(page);
      setExpandedEmailRowId(null);
      setExpandedPhoneRowId(null);
    } catch (e) {
      if (
        controller.signal.aborted ||
        e?.code === "ERR_CANCELED" ||
        requestId !== contactsRequestRef.current
      ) {
        return;
      }

      console.error(e);
      setError(getApiErrorText(e, t("error_loading_contacts")));
      scheduleContactsRetry(e, page, perPage, search);
    } finally {
      if (requestId === contactsRequestRef.current) {
        setLoading(false);
        if (contactsAbortRef.current === controller) {
          contactsAbortRef.current = null;
        }
      }
    }
  }

  useEffect(() => {
    fetchCustomFields();
    loadContactColumnSettings();
    if (academyContactQuery) {
      setSearchQuery(academyContactQuery);
    }
    fetchContacts(1, contactsPerPage, academyContactQuery);
    stopMicVisualization();
    return () => {
      contactsRequestRef.current += 1;
      contactsAbortRef.current?.abort();
      contactsAbortRef.current = null;
      clearContactsRetry();
      if (contactsSearchTimerRef.current) {
        window.clearTimeout(contactsSearchTimerRef.current);
        contactsSearchTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !academyContactQuery ||
      academyContactQueryRef.current === academyContactQuery
    ) {
      return;
    }

    academyContactQueryRef.current = academyContactQuery;
    setSearchQuery(academyContactQuery);
    fetchContacts(1, contactsPerPage, academyContactQuery);
    // The Academy route deliberately changes the query without remounting this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academyContactQuery]);

  useEffect(() => {
    if (!columnSettingsOpen) return undefined;
    const onPointerDown = (event) => {
      if (columnSettingsRef.current && !columnSettingsRef.current.contains(event.target)) {
        setColumnSettingsOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setColumnSettingsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [columnSettingsOpen]);

  const initTwilioDevice = async () => {
    const Twilio = window.Twilio;
    if (!Twilio || !Twilio.Device) {
      setTwilioError(t("twilio_sdk_missing"));
      return;
    }

    try {
      setTwilioLoading(true);
      setTwilioError("");

      const { data } = await api.get("/auth/telephony/token");
      const token = data.token;

      const device = new Twilio.Device(token, {
        codecPreferences: ["opus", "pcmu"],
        debug: false,
      });

      device.on("ready", () => setTwilioReady(true));

      device.on("error", (err) => {
        console.error("Twilio Device error:", err);
        setTwilioError(err.message || String(err));
        setIsCalling(false);
        stopMicVisualization();
      });

      device.on("connect", (conn) => {
        activeConnectionRef.current = conn;
        const sid = conn.parameters?.CallSid || "";
        setActiveCallSid(sid);
        setIsCalling(false);
      });

      device.on("disconnect", () => {
        activeConnectionRef.current = null;
        setActiveCallSid("");
        setIsCalling(false);
        setIsRecording(false);
        setActiveRecordingSid("");
        stopMicVisualization();
      });

      twilioDeviceRef.current = device;
    } catch (e) {
      console.error(e);
      setTwilioError(
        e.response?.data?.detail || e.message || t("error_init_twilio")
      );
    } finally {
      setTwilioLoading(false);
    }
  };

  useEffect(() => {
    initTwilioDevice();

    return () => {
      stopMicVisualization();
      if (twilioDeviceRef.current) {
        try {
          twilioDeviceRef.current.destroy();
        } catch (e) {
          console.error("Error destroying Twilio Device:", e);
        }
        twilioDeviceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (editingCompanyContactId == null) return;

    const updateDropdownPosition = () => {
      const anchor = companyEditorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const viewportPadding = 12;
      const gap = 8;
      const preferredWidth = 308;
      const preferredHeight = 350;
      const width = Math.min(
        preferredWidth,
        Math.max(240, window.innerWidth - viewportPadding * 2)
      );
      const left = Math.min(
        Math.max(rect.left, viewportPadding),
        window.innerWidth - width - viewportPadding
      );
      const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
      const spaceAbove = rect.top - gap - viewportPadding;
      const placement =
        spaceBelow >= Math.min(preferredHeight, 240) || spaceBelow >= spaceAbove
          ? "bottom"
          : "top";
      const availableHeight = placement === "bottom" ? spaceBelow : spaceAbove;
      const maxHeight = Math.max(150, Math.min(preferredHeight, availableHeight));

      setCompanyDropdownPosition({
        left,
        width,
        maxHeight,
        placement,
        top: placement === "bottom" ? rect.bottom + gap : "auto",
        bottom:
          placement === "top"
            ? window.innerHeight - rect.top + gap
            : "auto",
      });
    };

    const handleDown = (event) => {
      const clickedAnchor = companyEditorRef.current?.contains(event.target);
      const clickedDropdown = companyDropdownRef.current?.contains(event.target);
      if (!clickedAnchor && !clickedDropdown) closeCompanyEditor();
    };

    const handleKey = (event) => {
      if (event.key === "Escape") closeCompanyEditor();
    };

    const frameId = window.requestAnimationFrame(updateDropdownPosition);
    document.addEventListener("mousedown", handleDown);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);
    return () => {
      window.cancelAnimationFrame(frameId);
      document.removeEventListener("mousedown", handleDown);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingCompanyContactId]);

  const fetchCompanies = async () => {
    if (companies.length > 0 || companiesLoading) return;
    try {
      setCompaniesLoading(true);
      setCompaniesError("");
      const { data } = await api.get("/auth/company/all", {
        params: { skip: 0, limit: 1000 },
      });
      setCompanies(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setCompaniesError(
        e.response?.data?.detail || t("error_loading_companies")
      );
    } finally {
      setCompaniesLoading(false);
    }
  };


  const openCompanyEditor = async (contact) => {
    if (!contact?.id) return;
    setCompanyDropdownPosition(null);
    setEditingCompanyContactId(contact.id);
    setCompanySearch("");
    await fetchCompanies();
  };

  const closeCompanyEditor = () => {
    setEditingCompanyContactId(null);
    setCompanySearch("");
    setCompanyDropdownPosition(null);
  };

  const patchContactCompanyInState = (contactId, company) => {
    setContacts((prev) =>
      prev.map((item) => {
        if (item.id !== contactId) return item;
        if (!company) {
          return {
            ...item,
            company_id: null,
            company_ui_id: null,
            company_name: null,
          };
        }
        return {
          ...item,
          company_id: company.id,
          company_ui_id: getCompanyUiId(company),
          company_name: getCompanyLabel(company),
        };
      })
    );
  };

  const handleCompanySelect = async (contact, company) => {
    if (!contact?.id) return;
    const companyId = company?.id ?? null;

    try {
      setSavingCompanyContactId(contact.id);
      setError("");
      await api.put(`/contacts/${contact.id}`, { company_id: companyId });
      patchContactCompanyInState(contact.id, company || null);
      closeCompanyEditor();
    } catch (e) {
      console.error(e);
      setError(getApiErrorText(e, t("error_updating_contact", { defaultValue: "Failed to update contact" })));
    } finally {
      setSavingCompanyContactId(null);
    }
  };

  const renderCompanyCell = (contact) => {
    const isOpen = editingCompanyContactId === contact.id;
    const currentLabel = contact.company_name || (contact.company_ui_id ? `#${contact.company_ui_id}` : "");
    const filteredCompanies = companies.filter((company) => companyMatchesSearch(company, companySearch));

    return (
      <div className="contactsCompanyEditor" ref={isOpen ? companyEditorRef : null}>
        <button
          type="button"
          className={`contactsCompanyValue ${isOpen ? "isOpen" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (isOpen) closeCompanyEditor();
            else openCompanyEditor(contact);
          }}
          title={currentLabel || t("select_company", { defaultValue: "Select company" })}
        >
          {currentLabel ? (
            <>
              <span className="contactsCompanyId">#{contact.company_ui_id || contact.company_id}</span>
              <span className="contactsCompanyName">{contact.company_name || t("sales_contacts_company")}</span>
            </>
          ) : (
            <span className="contactsCompanyEmpty">{t("n_a")}</span>
          )}
        </button>

        {isOpen &&
          companyDropdownPosition &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              ref={companyDropdownRef}
              className={`contactsCompanyDropdown contactsCompanyDropdownPortalV2 is-${companyDropdownPosition.placement}`}
              style={{
                left: companyDropdownPosition.left,
                top: companyDropdownPosition.top,
                bottom: companyDropdownPosition.bottom,
                width: companyDropdownPosition.width,
                maxHeight: companyDropdownPosition.maxHeight,
                "--contacts-company-dropdown-max-height": `${companyDropdownPosition.maxHeight}px`,
              }}
              role="dialog"
              aria-label={t("select_company", {
                defaultValue: "Выбор компании",
              })}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="contactsCompanyDropdownHead">
                <div>
                  <small>{t("sales_contacts_company", { defaultValue: "Компания" })}</small>
                  <strong>{t("select_company", { defaultValue: "Выберите компанию" })}</strong>
                </div>
                <button
                  type="button"
                  className="contactsCompanyDropdownClose"
                  onClick={closeCompanyEditor}
                  aria-label={t("close", { defaultValue: "Закрыть" })}
                >
                  ×
                </button>
              </div>

              <div className="contactsCompanySearchWrap">
                <input
                  className="contactsCompanySearch"
                  autoFocus
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  placeholder={t("search", { defaultValue: "Поиск компании..." })}
                />
              </div>

              <div className="contactsCompanyOptions" aria-busy={companiesLoading}>
                <button
                  type="button"
                  className="contactsCompanyOption contactsCompanyOptionMuted"
                  disabled={savingCompanyContactId === contact.id}
                  onClick={() => handleCompanySelect(contact, null)}
                >
                  <span className="contactsCompanyOptionClearIcon" aria-hidden="true">×</span>
                  <span className="contactsCompanyOptionText">
                    {t("clear", { defaultValue: "Убрать компанию" })}
                  </span>
                </button>

                {companiesError ? (
                  <div className="contactsCompanyMessage isError" role="alert">
                    {companiesError}
                  </div>
                ) : companiesLoading ? (
                  <div className="contactsCompanyMessage">
                    <span className="contactsCompanySpinner" />
                    {t("loading", { defaultValue: "Загрузка..." })}
                  </div>
                ) : filteredCompanies.length > 0 ? (
                  filteredCompanies.map((company) => {
                    const selected = String(contact.company_id || "") === String(company.id || "");
                    return (
                      <button
                        type="button"
                        key={company.id}
                        className={`contactsCompanyOption ${selected ? "selected" : ""}`}
                        disabled={savingCompanyContactId === contact.id}
                        aria-pressed={selected}
                        onClick={() => handleCompanySelect(contact, company)}
                      >
                        <span className="contactsCompanyOptionId">#{getCompanyUiId(company)}</span>
                        <span className="contactsCompanyOptionText">{getCompanyLabel(company)}</span>
                        {selected && <span className="contactsCompanyOptionCheck" aria-hidden="true">✓</span>}
                      </button>
                    );
                  })
                ) : (
                  <div className="contactsCompanyMessage">
                    {t("no_results", { defaultValue: "Компании не найдены" })}
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
    );
  };

  const fetchSources = async () => {
    if (sources.length > 0 || sourcesLoading) return;
    try {
      setSourcesLoading(true);
      setSourcesError("");
      const { data } = await api.get("/sources", {
        params: { skip: 0, limit: 1000 },
      });
      setSources(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setSourcesError(e.response?.data?.detail || t("error_loading_sources"));
    } finally {
      setSourcesLoading(false);
    }
  };

  const applyContactsPerPage = (value) => {
    const perPage = Number(value);
    if (!perPage || perPage <= 0) return;
    setContactsPerPage(perPage);
    setCurrentPage(1);
    fetchContacts(1, perPage, searchQuery);
  };

  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });

    const valueForSort = (item, k) => {
      if (k === "email") return getEmailsString(item) || "";
      if (k === "phone") return getPhonesString(item) || "";
      if (k === "telegram") return getTelegramDisplay(item) || "";
      if (k === "contact_status") return item.status || "";
      if (k === "owner_id") return item.owner_display_name || item.owner_full_name || item.owner_email || item.owner_ui_id || item.owner_id || "";
      if (k.startsWith("custom_")) {
        const fieldKey = k.replace("custom_", "");
        return item?.custom_field_values?.[fieldKey] || "";
      }
      return item[k];
    };

    const sorted = [...contacts].sort((a, b) => {
      const av = valueForSort(a, key);
      const bv = valueForSort(b, key);

      if (av === undefined || av === null) return 1;
      if (bv === undefined || bv === null) return -1;

      if (av < bv) return direction === "asc" ? -1 : 1;
      if (av > bv) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setContacts(sorted);
  };

  const paginate = (pageNumber) => {
    fetchContacts(pageNumber, contactsPerPage, searchQuery);
  };

  const handleContactsPerPageChange = (e) => {
    const value = e.target.value;

    if (value === "custom") {
      const entered = window.prompt(
        t("enter_entries_per_page"),
        String(contactsPerPage)
      );

      if (!entered) {
        e.target.value = String(contactsPerPage);
        return;
      }

      const num = Number(entered);

      if (!num || num <= 0) {
        alert(t("invalid_entries_per_page"));
        e.target.value = String(contactsPerPage);
        return;
      }

      applyContactsPerPage(num);
      e.target.value = String(num);
    } else {
      applyContactsPerPage(value);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    if (contactsSearchTimerRef.current) {
      window.clearTimeout(contactsSearchTimerRef.current);
    }
    contactsSearchTimerRef.current = window.setTimeout(() => {
      contactsSearchTimerRef.current = null;
      fetchContacts(1, contactsPerPage, value);
    }, 300);
  };

  const showingText = t("showing_entries", {
    start: totalContacts === 0 ? 0 : (currentPage - 1) * contactsPerPage + 1,
    end: Math.min(currentPage * contactsPerPage, totalContacts),
    total: totalContacts,
  });

  const toggleRowSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const isRowSelected = (id) => selectedIds.includes(id);

  const allCurrentSelected =
    contacts.length > 0 && contacts.every((c) => selectedIds.includes(c.id));

  const toggleSelectAllCurrent = () => {
    const idsOnPage = contacts.map((c) => c.id);

    if (allCurrentSelected) {
      setSelectedIds((prev) => prev.filter((id) => !idsOnPage.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...idsOnPage])));
    }
  };

  const handleExportCsv = async () => {
    try {
      const { data } = await api.get("/contacts/export-csv", {
        params: {
          skip: 0,
          limit: 10000,
          q: searchQuery || undefined,
        },
        responseType: "blob",
      });

      const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "contacts_export.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || t("error_exporting_contacts"));
    }
  };

  const openDetailsModal = async (contactId, type) => {
    try {
      setDetailsError("");
      const { data } = await api.get(`/contacts/${contactId}`);
      setDetailsContact(data);
      setDetailsModalType(type);

      if (type === "email") {
        setDetailsItems(sortMainFirst(data.emails || []));
      } else {
        setDetailsItems(sortMainFirst(data.phones || []));
      }

      setDetailsModalOpen(true);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || t("error_loading_contact_details"));
    }
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    setDetailsContact(null);
    setDetailsItems([]);
    setDetailsError("");
  };

  const openCreateModal = () => {
    setCreateForm({
      full_name: "",
      company_id: "",
      owner_id: "",
      email: "",
      phone: "",
      custom_field_values: {},
    });
    setCreateError("");
    setCreateEmailError("");
    fetchCompanies();
    fetchCustomFields();
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setCreateError("");
    setCreateEmailError("");
    setCreateSaving(false);
  };

  const handleCreateChange = (field, value) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "email") {
      const emailVal = value.trim();
      if (emailVal && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
        setCreateEmailError(t("invalid_email"));
      } else {
        setCreateEmailError("");
      }
    }
  };

  const handleCreateCustomFieldChange = (fieldKey, value) => {
    setCreateForm((prev) => ({
      ...prev,
      custom_field_values: {
        ...(prev.custom_field_values || {}),
        [fieldKey]: value,
      },
    }));
  };

  const handleCreateSubmit = async () => {
    const fullName = createForm.full_name.trim();
    const email = createForm.email.trim();
    const phone = createForm.phone.trim();
    const companyIdRaw = createForm.company_id
      ? String(createForm.company_id).trim()
      : "";
    const ownerIdRaw = createForm.owner_id.trim();

    if (!fullName) {
      setCreateError(t("full_name_required"));
      return;
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setCreateError(t("invalid_email"));
      return;
    }

    if (phone && !/^\+?[0-9\s\-()]{6,20}$/.test(phone)) {
      setCreateError(t("invalid_phone"));
      return;
    }

    let company_id = null;
    let owner_id = null;

    if (companyIdRaw) {
      const n = Number(companyIdRaw);
      if (Number.isNaN(n)) {
        setCreateError(t("invalid_company_id"));
        return;
      }
      company_id = n;
    }

    if (ownerIdRaw) {
      const n = Number(ownerIdRaw);
      if (Number.isNaN(n)) {
        setCreateError(t("invalid_owner_id"));
        return;
      }
      owner_id = n;
    }

    for (const field of customFields) {
      if (!field.is_required) continue;

      const value = createForm.custom_field_values?.[field.key];

      if (value === "" || value === null || value === undefined) {
        setCreateError(t("field_required_error", { field: t(field.label || field.key) }));
        return;
      }
    }

    const customFieldValues = {};
    Object.entries(createForm.custom_field_values || {}).forEach(
      ([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          customFieldValues[key] = value;
        }
      }
    );

    const payload = {
      full_name: fullName,
      company_id,
      owner_id,
      custom_field_values: customFieldValues,
    };

    if (email) {
      payload.emails = [{ email, status: "main" }];
    }

    if (phone) {
      payload.phones = [{ phone, status: "main" }];
    }

    try {
      setCreateSaving(true);
      setCreateError("");

      await api.post("/contacts", payload);
      window.dispatchEvent(new Event("velsoft:academy-workflow-refresh"));

      setSuccess(t("contact_created"));
      closeCreateModal();
      await fetchContacts(currentPage, contactsPerPage, searchQuery);
    } catch (e) {
      console.error(e);
      setCreateError(getApiErrorText(e, t("error_creating_contact")));
    } finally {
      setCreateSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    const count = selectedIds.length;
    let previews = [];

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      previews = await Promise.all(
        selectedIds.map(async (id) => {
          const response = await api.get(`/contacts/${id}/delete-preview`);
          return response?.data || {};
        })
      );
    } catch (e) {
      console.error("Failed to load contact delete preview", e);
      setError(
        getApiErrorText(
          e,
          t("contact_delete_preview_error", {
            defaultValue:
              "Не удалось проверить связанные данные. Контакты не удалены.",
          })
        )
      );
      setLoading(false);
      return;
    }

    const totals = previews.reduce(
      (result, preview) => ({
        contacts: result.contacts + Number(preview?.contacts || 0),
        leads: result.leads + Number(preview?.leads || 0),
        tasks: result.tasks + Number(preview?.tasks || 0),
        deals: result.deals + Number(preview?.deals || 0),
        activities: result.activities + Number(preview?.activities || 0),
        files: result.files + Number(preview?.files || 0),
      }),
      {
        contacts: 0,
        leads: 0,
        tasks: 0,
        deals: 0,
        activities: 0,
        files: 0,
      }
    );

    const selectedContact =
      count === 1
        ? contacts.find((contact) => contact.id === selectedIds[0])
        : null;
    const heading = selectedContact
      ? t("confirm_delete_contact_name", {
          name:
            selectedContact.full_name || `#${selectedContact.id}`,
          defaultValue:
            "Контакт {{name}} будет удалён вместе со всей связанной CRM-цепочкой.",
        })
      : t("confirm_delete_contacts_chain", {
          count,
          defaultValue:
            "Выбранные контакты ({{count}}) будут удалены вместе со всеми связанными CRM-данными.",
        });
    const confirmationLines = [
      heading,
      "",
      t("will_be_deleted", { defaultValue: "Будет удалено:" }),
      `• ${t("sales_contacts", { defaultValue: "Контакты" })}: ${
        totals.contacts || count
      }`,
      `• ${t("sales_leads", { defaultValue: "Лиды" })}: ${totals.leads}`,
      `• ${t("sales_tasks", { defaultValue: "Задачи" })}: ${totals.tasks}`,
      `• ${t("sales_deals", { defaultValue: "Сделки" })}: ${totals.deals}`,
      `• ${t("sales_activities", { defaultValue: "Активности" })}: ${
        totals.activities
      }`,
      `• ${t("files", { defaultValue: "Файлы" })}: ${totals.files}`,
      "",
      t("delete_cannot_be_undone", {
        defaultValue: "Это действие нельзя отменить.",
      }),
    ];

    const confirmed = window.confirm(confirmationLines.join("\n"));
    if (!confirmed) {
      setLoading(false);
      return;
    }

    try {
      await Promise.all(selectedIds.map((id) => api.delete(`/contacts/${id}`)));

      setSuccess(t("contacts_deleted", { count }));

      await fetchContacts(currentPage, contactsPerPage, searchQuery);
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.detail || t("error_deleting_selected_contacts")
      );
    } finally {
      setLoading(false);
    }
  };

  const getMainEmailFromContact = (contact) => {
    if (!contact?.emails || contact.emails.length === 0) return "";
    const sorted = sortMainFirst(contact.emails);
    return sorted[0]?.email || "";
  };

  const getMainPhoneFromContact = (contact) => {
    if (!contact?.phones || contact.phones.length === 0) return "";
    const sorted = sortMainFirst(contact.phones);
    return sorted[0]?.phone || "";
  };

  const openSendEmailModal = (contact, clickedEmail) => {
    const toEmail = (
      clickedEmail || getMainEmailFromContact(contact) || ""
    ).trim();

    setSendEmailContact(contact || null);
    setSendEmailForm({
      to: toEmail,
      subject: "",
      template: "no_template",
      html_body: "",
      sender: "auto",
    });
    setSendEmailErrors({});
    setSendEmailSending(false);
    setAllowSenderOverrideSingle(false);
    setSendEmailAiTone("professional");
    setSendEmailAiObjective("follow_up");
    setSendEmailModalOpen(true);
  };

  const closeSendEmailModal = () => {
    setSendEmailModalOpen(false);
    setSendEmailContact(null);
    setSendEmailErrors({});
    setSendEmailSending(false);
    setAllowSenderOverrideSingle(false);
  };

  const handleSendEmailChange = (field, value) => {
    setSendEmailForm((prev) => ({ ...prev, [field]: value }));
    setSendEmailErrors((prev) => ({
      ...prev,
      [field]: undefined,
      form: undefined,
    }));
  };

  const handleGenerateAiDraft = async () => {
    if (!sendEmailContact?.id) return;

    try {
      setSendEmailDraftLoading(true);
      setSendEmailErrors((prev) => ({ ...prev, form: undefined }));

      const data = await aiApi.generateEmailDraft(sendEmailContact.id, {
        tone: sendEmailAiTone,
        objective: sendEmailAiObjective,
      });

      setSendEmailForm((prev) => ({
        ...prev,
        subject: data?.subject || prev.subject,
        html_body: data?.body || prev.html_body,
      }));
    } catch (e) {
      console.error(e);
      setSendEmailErrors((prev) => ({
        ...prev,
        form: e?.response?.data?.detail || t("error_generate_ai_draft"),
      }));
    } finally {
      setSendEmailDraftLoading(false);
    }
  };

  useEffect(() => {
    if (!sendEmailModalOpen) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await emailApi.getMode();
        if (cancelled) return;

        const allow = !!res?.allow_sender_override;
        setAllowSenderOverrideSingle(allow);

        if (!allow) {
          setSendEmailForm((prev) => ({ ...prev, sender: "auto" }));
        } else {
          setSendEmailForm((prev) => ({
            ...prev,
            sender: prev?.sender ? prev.sender : "auto",
          }));
        }
      } catch {
        if (cancelled) return;
        setAllowSenderOverrideSingle(false);
        setSendEmailForm((prev) => ({ ...prev, sender: "auto" }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sendEmailModalOpen]);

  const handleSendEmailSubmit = async () => {
    const errors = {};
    if (!sendEmailForm.to.trim()) errors.to = t("required") || "Required";
    if (!sendEmailForm.subject.trim()) {
      errors.subject = t("required") || "Required";
    }

    const htmlRequired =
      sendEmailForm.template === "no_template" || !sendEmailForm.template;

    if (htmlRequired && !sendEmailForm.html_body.trim()) {
      errors.html_body = t("required") || "Required";
    }

    if (Object.keys(errors).length > 0) {
      setSendEmailErrors(errors);
      return;
    }

    try {
      setSendEmailSending(true);
      setSendEmailErrors((prev) => ({ ...prev, form: undefined }));

      await emailApi.sendSingle({
        contact_id: sendEmailContact?.id ?? null,
        to_email: sendEmailForm.to.trim(),
        subject: sendEmailForm.subject.trim(),
        body: sendEmailForm.html_body,
        sender: sendEmailForm.sender || "auto",
      });

      setSuccess(t("email_sent"));
      closeSendEmailModal();
    } catch (e) {
      console.error(e);
      setSendEmailErrors((prev) => ({
        ...prev,
        form: e.response?.data?.detail || t("error_sending_email"),
      }));
    } finally {
      setSendEmailSending(false);
    }
  };

  const handleBulkEmailChange = (field, value) => {
    setBulkEmailForm((prev) => ({ ...prev, [field]: value }));
    setBulkEmailErrors((prev) => ({
      ...prev,
      [field]: undefined,
      form: undefined,
    }));
  };

  const recomputeBulkToPreview = (recips) => {
    const emails = recips.map((r) => (r.selected || "").trim()).filter(Boolean);

    setBulkEmailForm((prev) => ({
      ...prev,
      to_preview: emails.join(", "),
    }));
  };

  const openBulkEmailModal = () => {
    const selectedContacts = contacts.filter((c) => selectedIds.includes(c.id));

    const recips = selectedContacts.map((c) => {
      const emailsSorted = sortMainFirst(c.emails || []);
      const emails = emailsSorted.map((e) => e.email);
      const main = getMainEmailFromContact(c) || emails[0] || "";
      return {
        contactId: c.id,
        full_name: c.full_name,
        emails,
        selected: main,
      };
    });

    setBulkRecipients(recips);
    recomputeBulkToPreview(recips);

    setBulkEmailForm((prev) => ({
      ...prev,
      subject: "",
      template: "no_template",
      html_body: "",
      tag: "",
      is_test: false,
      sender: "auto",
    }));

    setBulkEmailErrors({});
    setBulkEmailSending(false);
    setBulkEmailModalOpen(true);
  };

  const closeBulkEmailModal = () => {
    setBulkEmailModalOpen(false);
    setBulkEmailErrors({});
    setBulkEmailSending(false);
    setBulkRecipients([]);
  };

  const handleBulkRecipientEmailChange = (contactId, email) => {
    setBulkRecipients((prev) => {
      const updated = prev.map((r) =>
        r.contactId === contactId ? { ...r, selected: email } : r
      );
      recomputeBulkToPreview(updated);
      return updated;
    });
  };

  const handleBulkEmailSubmit = async () => {
    const errors = {};

    if (selectedIds.length < 2) errors.form = t("bulk_email_need_two");
    if (!bulkEmailForm.subject.trim()) {
      errors.subject = t("required") || "Required";
    }

    const htmlBulkRequired =
      bulkEmailForm.template === "no_template" || !bulkEmailForm.template;

    if (htmlBulkRequired && !bulkEmailForm.html_body.trim()) {
      errors.html_body = t("required") || "Required";
    }

    if (Object.keys(errors).length > 0) {
      setBulkEmailErrors(errors);
      return;
    }

    const recipientsPayload = bulkRecipients
      .filter((r) => r.selected)
      .map((r) => ({
        contact_id: r.contactId,
        email: r.selected,
      }));

    try {
      setBulkEmailSending(true);
      setBulkEmailErrors((prev) => ({ ...prev, form: undefined }));

      await emailApi.sendCorporate({
        contact_ids: selectedIds,
        subject: bulkEmailForm.subject.trim(),
        body: bulkEmailForm.html_body,
        template:
          bulkEmailForm.template === "no_template"
            ? null
            : bulkEmailForm.template,
        tag: bulkEmailForm.tag || null,
        is_test: bulkEmailForm.is_test,
        recipients: recipientsPayload,
        sender: bulkEmailForm.sender || "auto",
      });

      setSuccess(t("corporate_email_sent"));
      closeBulkEmailModal();
    } catch (e) {
      console.error(e);
      setBulkEmailErrors((prev) => ({
        ...prev,
        form: e.response?.data?.detail || t("error_sending_corporate_email"),
      }));
    } finally {
      setBulkEmailSending(false);
    }
  };

  const getLeadCounts = (c) => {
    const total =
      typeof c?.leads_total === "number"
        ? c.leads_total
        : c?.last_lead_id
          ? 1
          : 0;

    const active =
      typeof c?.leads_active === "number"
        ? c.leads_active
        : c?.last_lead_status &&
            !["disqualified", "converted"].includes(c.last_lead_status)
          ? 1
          : 0;

    return { total, active };
  };

  const openLeadModal = (contact) => {
    const academyLeadTitle = academyTablesActive
      ? academyTableParams.get("academy_lead_title") || ""
      : "";
    const academyLeadDescription = academyTablesActive
      ? academyTableParams.get("academy_lead_description") || ""
      : "";
    setLeadModalContact(contact);
    setLeadForm({
      title: academyLeadTitle || contact.full_name || "",
      source_id: "",
      budget: "",
      description: academyLeadDescription,
    });
    setLeadError("");
    fetchSources();
    setLeadModalOpen(true);
  };

  const closeLeadModal = () => {
    setLeadModalOpen(false);
    setLeadModalContact(null);
    setLeadError("");
    setLeadSaving(false);
  };

  const handleLeadChange = (field, value) => {
    setLeadForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLeadSubmit = async () => {
    if (!leadModalContact) return;

    const title = leadForm.title.trim();
    const sourceIdRaw = String(leadForm.source_id || "").trim();
    const budgetRaw = String(leadForm.budget || "").trim();
    const description = leadForm.description.trim();

    if (!title) {
      setLeadError(t("lead_title_required"));
      return;
    }

    if (!sourceIdRaw) {
      setLeadError(
        t("lead_source_required", {
          defaultValue: "Выберите источник лида.",
        }),
      );
      return;
    }

    const source_id = Number(sourceIdRaw);
    if (!Number.isInteger(source_id) || source_id <= 0) {
      setLeadError(t("invalid_source_id"));
      return;
    }

    let budget = null;
    if (budgetRaw) {
      const n = Number(budgetRaw);
      if (Number.isNaN(n)) {
        setLeadError(t("invalid_budget"));
        return;
      }
      budget = n;
    }

    const payload = {
      title,
      contact_id: leadModalContact.id,
      source_id,
      budget,
      description: description || null,
    };

    try {
      setLeadSaving(true);
      setLeadError("");

      const response = await api.post("/leads", payload);
      const createdLead = response?.data;
      window.dispatchEvent(new Event("velsoft:academy-workflow-refresh"));

      setSuccess(t("lead_created"));
      closeLeadModal();

      if (createdLead?.id || createdLead?.ui_id) {
        const params = new URLSearchParams();

        params.set("contact_id", String(leadModalContact.id));

        if (createdLead?.id) {
          params.set("lead_id", String(createdLead.id));
        }

        if (createdLead?.ui_id) {
          params.set("lead_ui_id", String(createdLead.ui_id));
        }

        if (academyTablesActive) {
          params.set("academy_tables", "1");
          [
            "academy_task_title",
            "academy_task_description",
            "academy_task_due",
          ].forEach((key) => {
            const value = academyTableParams.get(key);
            if (value) params.set(key, value);
          });
        }

        navigate(`/menu/sales/leads?${params.toString()}`);
        return;
      }

      await fetchContacts(currentPage, contactsPerPage, searchQuery);
    } catch (e) {
      console.error(e);
      setLeadError(e.response?.data?.detail || t("error_creating_lead"));
    } finally {
      setLeadSaving(false);
    }
  };

  const openCallModal = (contact, phone) => {
    setCallContact(contact);
    setCallPhone((phone || getMainPhoneFromContact(contact) || "").trim());
    setActiveCallSid("");
    setCallError("");
    setIsMuted(false);
    setIsOnHold(false);
    setIsRecording(false);
    setActiveRecordingSid("");
    setCallModalOpen(true);
  };

  const closeCallModal = () => {
    if (isCalling) return;
    setCallModalOpen(false);
    setCallContact(null);
    setCallPhone("");
    setActiveCallSid("");
    setCallError("");
    setIsMuted(false);
    setIsOnHold(false);
    setIsRecording(false);
    setActiveRecordingSid("");
    stopMicVisualization();
  };

  const handleStartCall = () => {
    const phone = callPhone.trim();
    if (!phone) {
      setCallError(t("phone_required"));
      return;
    }

    if (!twilioDeviceRef.current) {
      setCallError(t("twilio_device_not_ready"));
      return;
    }

    setIsCalling(true);
    setCallError("");

    const params = {
      To: phone,
      contact_id: callContact?.id ?? "",
    };

    try {
      const conn = twilioDeviceRef.current.connect(params);
      activeConnectionRef.current = conn;

      startMicVisualization();

      conn.on("accept", () => {
        setIsCalling(false);
        const sid = conn.parameters?.CallSid || "";
        setActiveCallSid(sid);
      });

      conn.on("error", (err) => {
        console.error("Twilio connection error:", err);
        setCallError(err.message || t("error_start_call"));
        setIsCalling(false);
        stopMicVisualization();
      });

      conn.on("disconnect", () => {
        activeConnectionRef.current = null;
        setActiveCallSid("");
        setIsCalling(false);
        setIsRecording(false);
        setActiveRecordingSid("");
        stopMicVisualization();
      });
    } catch (e) {
      console.error(e);
      setCallError(e.message || t("error_trying_call"));
      setIsCalling(false);
    }
  };

  const handleHangupCall = () => {
    const conn =
      activeConnectionRef.current ||
      (twilioDeviceRef.current &&
        twilioDeviceRef.current.activeConnection &&
        twilioDeviceRef.current.activeConnection());

    if (!conn) return;

    try {
      conn.disconnect();
    } catch (e) {
      console.error(e);
      setCallError(e.message || t("error_hangup_call"));
    }
  };

  const handleToggleMute = async () => {
    if (!activeConnectionRef.current) return;

    const newMuted = !isMuted;
    setIsMuted(newMuted);

    try {
      activeConnectionRef.current.mute(newMuted);
    } catch {
      // noop
    }
  };

  const handleToggleHold = async () => {
    if (!activeCallSid) return;
    setIsOnHold((prev) => !prev);
  };

  const handleToggleRecord = async () => {
    if (!activeCallSid) return;

    try {
      setCallError("");

      if (!isRecording) {
        const { data } = await api.post("/auth/telephony/record/start", {
          call_sid: activeCallSid,
        });

        setActiveRecordingSid(data.recording_sid);
        setIsRecording(true);
        return;
      }

      if (isRecording && activeRecordingSid) {
        await api.post("/auth/telephony/record/stop", {
          recording_sid: activeRecordingSid,
        });

        setIsRecording(false);
        setActiveRecordingSid("");
      }
    } catch (e) {
      console.error(e);
      setCallError(e.response?.data?.detail || t("error_toggle_recording"));
    }
  };

  const startMicVisualization = async () => {
    try {
      if (audioContextRef.current) return;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext =
        new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;
      mediaStreamRef.current = stream;

      const tick = () => {
        if (!analyserRef.current || !dataArrayRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArrayRef.current);

        let sum = 0;
        for (let i = 0; i < dataArrayRef.current.length; i++) {
          sum += dataArrayRef.current[i];
        }

        const avg = sum / dataArrayRef.current.length;
        const level = avg / 255;

        setMicLevel(level);
        animationFrameRef.current = requestAnimationFrame(tick);
      };

      tick();
    } catch (err) {
      console.error("Mic access error:", err);
    }
  };

  const stopMicVisualization = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }

    analyserRef.current = null;
    dataArrayRef.current = null;
    setMicLevel(0);
  };

  const totalPages = Math.ceil(totalContacts / contactsPerPage) || 1;

  const hasMobileOverlayOpen =
    detailsModalOpen ||
    createModalOpen ||
    sendEmailModalOpen ||
    bulkEmailModalOpen ||
    bulkMarketingModalOpen ||
    leadModalOpen ||
    marketingModalOpen ||
    callModalOpen;

  useEffect(() => {
    document.body.classList.toggle(
      "contacts-mobile-overlay-open",
      hasMobileOverlayOpen
    );

    return () => {
      document.body.classList.remove("contacts-mobile-overlay-open");
    };
  }, [hasMobileOverlayOpen]);

  return (
    <div className="contactsPageRoot row mx-md-n5">
      <h1 data-academy="contacts-header" className="mb-4">{t("sales_contacts")}</h1>

      {(totalContacts > 0 || searchQuery.trim()) && (
        <div data-academy="contacts-import-export" className="mb-3 d-flex gap-3 align-items-end">
          <div className="ms-auto d-flex align-items-center gap-2 flex-wrap">
            {getUserPermissions()?.includes("contacts.import") && (
              <button
                className="btn btn-outline-primary"
                type="button"
                onClick={() => navigate("/menu/sales/contacts/import")}
              >
                {t("import_csv", { defaultValue: "Импорт CSV" })}
              </button>
            )}

            {getUserPermissions()?.includes("contacts.export") && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                disabled={contacts.length === 0}
                onClick={handleExportCsv}
              >
                {t("export_csv")}
                {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="p-5 border bg-light w-100">
        {(totalContacts > 0 || searchQuery.trim()) && (
          <div data-academy="contacts-search" className="d-flex bd-highlight">
            <div className="mb-3 p-2 flex-grow-1 bd-highlight">
              <label className="form-label">{t("search")}</label>
              <input
                type="text"
                className="form-control"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t("sales_contacts_search_placeholder")}
              />
            </div>
          </div>
        )}

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        {loading ? (
          <p>{t("loading")}...</p>
        ) : contacts.length > 0 ? (
          <>
            <div className="row mb-3">
              <div className="col-md-3">
                <div className="d-flex mb-3 p-2 flex-grow-1 bd-highlight align-items-center gap-2">
                  <select
                    title={t("entries_per_page")}
                    className="form-select w-auto"
                    value={contactsPerPage}
                    onChange={handleContactsPerPageChange}
                    style={{ width: "100px" }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value="custom">
                      {t("entries_per_page_custom")}
                    </option>
                  </select>
                </div>
              </div>

              <div data-academy="contacts-actions" className="col-md-9 d-flex justify-content-end align-items-end gap-2">
                {selectedIds.length === 0 &&
                  getUserPermissions()?.includes("contacts.create") && (
                    <button
                      data-academy="contacts-create"
                      className="btn btn-success mb-4 btn-sm"
                      type="button"
                      onClick={openCreateModal}
                    >
                      {t("create")}
                    </button>
                  )}

                {selectedIds.length >= 2 &&
                  getUserPermissions()?.includes("email.send") && (
                    <button
                      className="btn btn-primary mb-4 btn-sm"
                      type="button"
                      onClick={openBulkEmailModal}
                    >
                      {t("send_corporate_email")}
                      {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
                    </button>
                  )}

                {selectedIds.length > 0 &&
                  getUserPermissions()?.includes("marketing.send") && (
                    <button
                      className="btn btn-info mb-4 btn-sm contactsBulkMarketingBtn"
                      type="button"
                      onClick={openBulkMarketingModal}
                    >
                      {t("contacts_marketing_selected")}{selectedIds.length})
                    </button>
                  )}

                {selectedIds.length > 0 &&
                  getUserPermissions()?.includes("contacts.delete") && (
                    <button
                      className="btn btn-danger mb-4 btn-sm"
                      type="button"
                      onClick={handleBulkDelete}
                    >
                      {t("delete")} ({selectedIds.length})
                    </button>
                  )}

                {selectedIds.length > 0 &&
                  getUserPermissions()?.includes("tasks.create") && (
                    <button
                      className="btn btn-outline-secondary mb-4 btn-sm"
                      type="button"
                      onClick={() =>
                        navigate("/menu/sales/tasks", {
                          state: { contact_ids: selectedIds },
                        })
                      }
                    >
                      {t("create_task")}{" "}
                      {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
                    </button>
                  )}

                <div className="contactsColumnSettings" ref={columnSettingsRef}>
                  <button
                    className="btn btn-outline-secondary mb-4 btn-sm contactsColumnsButton"
                    type="button"
                    aria-expanded={columnSettingsOpen}
                    onClick={() => setColumnSettingsOpen((value) => !value)}
                  >
                    <span aria-hidden="true">☷</span>
                    <span>{t("columns", { defaultValue: "Колонки" })}</span>
                  </button>

                  {columnSettingsOpen && (
                    <div className="contactsColumnSettingsPanel">
                      <div className="contactsColumnSettingsHeader">
                        <div>
                          <strong>{t("table_columns", { defaultValue: "Колонки таблицы" })}</strong>
                          <small>{t("table_columns_hint", { defaultValue: "Показывайте только нужные поля. Изменения сохраняются автоматически." })}</small>
                        </div>
                        {columnSettingsSaving && <span className="contactsColumnSaving">{t("contacts_saving")}</span>}
                      </div>

                      <div className="contactsColumnSettingsList">
                        {contactColumnOptions.map((option) => (
                          <label className="contactsColumnOption" key={option.key}>
                            <input
                              type="checkbox"
                              checked={isColumnVisible(option.key, option.defaultVisible)}
                              disabled={columnSettingsLoading || columnSettingsSaving}
                              onChange={() => toggleContactColumn(option)}
                            />
                            <span className="contactsColumnOptionLabel">{option.label}</span>
                            {option.custom && <span className="contactsColumnOptionType">{t("custom")}</span>}
                          </label>
                        ))}
                      </div>

                      {columnSettingsError && <div className="contactsColumnSettingsError">{columnSettingsError}</div>}

                      <div className="contactsColumnSettingsFooter">
                        <span>
                          {contactColumnOptions.filter((option) => isColumnVisible(option.key, option.defaultVisible)).length}
                          {" / "}{contactColumnOptions.length}
                        </span>
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={columnSettingsSaving} onClick={resetContactColumns}>
                          {t("reset", { defaultValue: "Сбросить" })}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  className="btn btn-primary mb-4 btn-sm"
                  type="button"
                  onClick={() =>
                    fetchContacts(currentPage, contactsPerPage, searchQuery)
                  }
                >
                  {t("refresh")}
                </button>
              </div>
            </div>

            <div className="contactsMobileList">
              {contacts.map((c) => {
                const emailsSorted = sortMainFirst(c.emails || []);
                const phonesSorted = sortMainFirst(c.phones || []);
                const mainEmail = emailsSorted[0]?.email || "";
                const mainPhone = phonesSorted[0]?.phone || "";
                const { total: leadsTotal, active: leadsActive } = getLeadCounts(c);

                return (
                  <article
                    key={c.id}
                    className={`contactsMobileCard ${
                      isRowSelected(c.id) ? "contactsMobileCardSelected" : ""
                    }`}
                  >
                    <div className="contactsMobileCardTop">
                      <label className="contactsMobileCheck">
                        <input
                          type="checkbox"
                          checked={isRowSelected(c.id)}
                          onChange={() => toggleRowSelected(c.id)}
                        />
                      </label>

                      <div className="contactsMobileIdentity">
                        <div className="contactsMobileName">
                          {renderInlineContactField(c, "full_name", true)}
                        </div>
                        <div className="contactsMobileMeta">
                          ID: {c.ui_id || c.id}
                          {c.company_ui_id ? ` · ${t("sales_contacts_company")}: ${c.company_ui_id}` : ""}
                        </div>
                      </div>

                      {c.status ? (
                        <span
                          className={`badge rounded-pill contactsMobileStatus ${
                            contactStatusClassMap[c.status] || "bg-secondary"
                          }`}
                        >
                          {c.status}
                        </span>
                      ) : null}
                    </div>

                    <div className="contactsMobileInfoGrid">
                      <button
                        type="button"
                        className="contactsMobileInfoItem"
                        onClick={() => openDetailsModal(c.id, "email")}
                      >
                        <span className="contactsMobileInfoLabel">
                          {t("sales_contacts_email")}
                        </span>
                        <span className="contactsMobileInfoValue">
                          {mainEmail || t("n_a")}
                        </span>
                      </button>

                      <button
                        type="button"
                        className="contactsMobileInfoItem"
                        onClick={() => openDetailsModal(c.id, "phone")}
                      >
                        <span className="contactsMobileInfoLabel">
                          {t("sales_contacts_phone")}
                        </span>
                        <span className="contactsMobileInfoValue">
                          {mainPhone || t("n_a")}
                        </span>
                      </button>

                      <div className="contactsMobileInfoItem contactsMobileInfoItem--editable">
                        <span className="contactsMobileInfoLabel">{t("source_preset_telegram")}</span>
                        <div className="contactsMobileInfoValue">
                          {renderInlineContactField(c, "telegram_username", true)}
                        </div>
                      </div>
                    </div>

                    {visibleCustomFields.length > 0 && (
                      <div className="contactsMobileCustomFields">
                        {visibleCustomFields.slice(0, 3).map((field) => (
                          <div key={field.id} className="contactsMobileCustomField">
                            <span>{t(field.label || field.key)}</span>
                            <strong>{getCustomValue(c, field)}</strong>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="contactsMobileStats">
                      <button
                        type="button"
                        className="contactsMobileStat"
                        disabled={leadsTotal === 0}
                        onClick={() =>
                          navigate(
                            `/menu/sales/leads?contact_id=${encodeURIComponent(c.id)}`
                          )
                        }
                      >
                        <span>{t("leads")}</span>
                        <strong>{leadsTotal || 0}</strong>
                      </button>

                      <div className="contactsMobileStat">
                        <span>{t("active")}</span>
                        <strong>{leadsActive || 0}</strong>
                      </div>

                      <div className="contactsMobileStat">
                        <span>{t("sales_contacts_owner")}</span>
                        <strong>{renderOwnerCell(c)}</strong>
                      </div>
                    </div>

                    <div className="contactsMobileActions">
                      <button
                        data-academy="contact-details"
                        data-academy-contact-name={c.full_name || ""}
                        type="button"
                        className="btn btn-warning btn-sm"
                        onClick={() =>
                          navigate(`/menu/sales/contacts/${c.id}/personal-details`)
                        }
                      >
                        {t("details")}
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-info btn-sm"
                        onClick={() => openMarketingModal(c)}
                      >
                        {t("marketing")}
                      </button>

                      {mainEmail && getUserPermissions()?.includes("email.send") && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => openSendEmailModal(c, mainEmail)}
                        >
                          {t("email")}
                        </button>
                      )}

                      {mainPhone && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => openCallModal(c, mainPhone)}
                        >
                          {t("call")}
                        </button>
                      )}

                      {getUserPermissions()?.includes("leads.create") && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          data-academy="contact-create-lead"
                          data-academy-entity-title={c.full_name || ""}
                          onClick={() => openLeadModal(c)}
                        >
                          + {t("create_lead")}
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>

            <div data-academy="contacts-table" className="contactsTableCard">
              <div className="contactsTableScroll">
                <table className="table table-striped table-hover contactsTable">
                  <thead className="thead-dark">
                    <tr>
                      <th className="contactsStickySelect">
                        <input
                          type="checkbox"
                          checked={allCurrentSelected}
                          onChange={toggleSelectAllCurrent}
                        />
                      </th>

                      {isColumnVisible("ui_id", true) && (


                      <th
                        className="contactsStickyId"
                        onClick={() => sortData("id")}
                      >
                        {t("id")}{" "}
                        {sortConfig.key === "id" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>


                      )}

                      {isColumnVisible("full_name", true) && (


                      <th onClick={() => sortData("full_name")}>
                        {t("sales_contacts_name")}{" "}
                        {sortConfig.key === "full_name" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>


                      )}

                      {isColumnVisible("email", true) && (


                      <th onClick={() => sortData("email")}>
                        {t("sales_contacts_email")}{" "}
                        {sortConfig.key === "email" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>


                      )}

                      {isColumnVisible("phone", true) && (


                      <th onClick={() => sortData("phone")}>
                        {t("sales_contacts_phone")}{" "}
                        {sortConfig.key === "phone" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>


                      )}

                      {isColumnVisible("telegram", true) && (


                      <th onClick={() => sortData("telegram")}>
                        {t("source_preset_telegram")} {" "}
                        {sortConfig.key === "telegram" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>


                      )}

                      {visibleCustomFields.map((field) => (
                        <th
                          key={field.id}
                          onClick={() => sortData(`custom_${field.key}`)}
                        >
                          {t(field.label || field.key)}{" "}
                          {sortConfig.key === `custom_${field.key}` &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                      ))}

                      {isColumnVisible("contact_status", true) && (


                      <th onClick={() => sortData("contact_status")}>
                        {t("contact_status")}{" "}
                        {sortConfig.key === "contact_status" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>


                      )}

                      {isColumnVisible("company_id", true) && (


                      <th onClick={() => sortData("company_id")}>
                        {t("sales_contacts_company")}{" "}
                        {sortConfig.key === "company_id" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>


                      )}

                      {isColumnVisible("owner_id", true) && (


                      <th onClick={() => sortData("owner_id")}>
                        {t("sales_contacts_owner")}{" "}
                        {sortConfig.key === "owner_id" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>


                      )}

                      {isColumnVisible("created_at", true) && (


                      <th onClick={() => sortData("created_at")}>
                        {t("created_at")}{" "}
                        {sortConfig.key === "created_at" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>


                      )}

                      <th className="contactsStickyActions">{t("actions")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {contacts.map((c) => {
                      const emailsSorted = sortMainFirst(c.emails || []);
                      const phonesSorted = sortMainFirst(c.phones || []);

                      const isEmailExpanded = expandedEmailRowId === c.id;
                      const isPhoneExpanded = expandedPhoneRowId === c.id;

                      const emailsToShow = isEmailExpanded
                        ? emailsSorted
                        : emailsSorted.slice(0, 2);
                      const phonesToShow = isPhoneExpanded
                        ? phonesSorted
                        : phonesSorted.slice(0, 2);

                      const hiddenEmailsCount = Math.max(
                        0,
                        emailsSorted.length - emailsToShow.length
                      );
                      const hiddenPhonesCount = Math.max(
                        0,
                        phonesSorted.length - phonesToShow.length
                      );

                      const { total: leadsTotal, active: leadsActive } =
                        getLeadCounts(c);

                      return (
                        <tr key={c.id}>
                          <td className="contactsStickySelect">
                            <input
                              type="checkbox"
                              checked={isRowSelected(c.id)}
                              onChange={() => toggleRowSelected(c.id)}
                            />
                          </td>

                          {isColumnVisible("ui_id", true) && (


                          <td className="contactsStickyId">{c.ui_id}</td>


                          )}
                          {isColumnVisible("full_name", true) && (

                          <td>{renderInlineContactField(c, "full_name")}</td>

                          )}

                          {isColumnVisible("email", true) && (
                          <td
                            style={{ cursor: "pointer" }}
                            onClick={() => openDetailsModal(c.id, "email")}
                          >
                            {emailsSorted.length > 0 ? (
                              <div className="d-flex flex-wrap align-items-center">
                                {emailsToShow.map((e) => (
                                  <span
                                    key={e.id}
                                    className={
                                      "badge rounded-pill me-1 " +
                                      (e.status === "main"
                                        ? "bg-primary"
                                        : "bg-secondary")
                                    }
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      openSendEmailModal(c, e.email);
                                    }}
                                  >
                                    {e.email}
                                  </span>
                                ))}

                                {hiddenEmailsCount > 0 && (
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 ms-1"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleEmailExpand(c.id);
                                    }}
                                  >
                                    <span className="badge rounded-pill bg-dark">
                                      +{hiddenEmailsCount}
                                    </span>
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">{t("n_a")}</span>
                            )}
                          </td>

                          )}

                          {isColumnVisible("phone", true) && (
                          <td
                            style={{ cursor: "pointer" }}
                            onClick={() => openDetailsModal(c.id, "phone")}
                          >
                            {phonesSorted.length > 0 ? (
                              <div className="d-flex flex-wrap align-items-center">
                                {phonesToShow.map((p) => (
                                  <span
                                    key={p.id}
                                    className={
                                      "badge rounded-pill me-1 " +
                                      (p.status === "main"
                                        ? "bg-success"
                                        : "bg-secondary")
                                    }
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      openCallModal(c, p.phone);
                                    }}
                                  >
                                    {p.phone}
                                  </span>
                                ))}

                                {hiddenPhonesCount > 0 && (
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 ms-1"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      togglePhoneExpand(c.id);
                                    }}
                                  >
                                    <span className="badge rounded-pill bg-dark">
                                      +{hiddenPhonesCount}
                                    </span>
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">{t("n_a")}</span>
                            )}
                          </td>

                                                    )}

{isColumnVisible("telegram", true) && (


                          <td>{renderInlineContactField(c, "telegram_username")}</td>


                          )}

                          {visibleCustomFields.map((field) => (
                            <td key={field.id}>{getCustomValue(c, field)}</td>
                          ))}

                          {isColumnVisible("contact_status", true) && (


                          <td>
                            {c.status ? (
                              <span
                                className={`badge rounded-pill ${
                                  contactStatusClassMap[c.status] ||
                                  "bg-secondary"
                                }`}
                              >
                                {c.status}
                              </span>
                            ) : (
                              <span className="text-muted">{t("n_a")}</span>
                            )}
                          </td>


                          )}

                          {isColumnVisible("company_id", true) && (


                          <td className="contactsCompanyCell">{renderCompanyCell(c)}</td>


                          )}
                          {isColumnVisible("owner_id", true) && (

                          <td>{renderOwnerCell(c)}</td>

                          )}

                          {isColumnVisible("created_at", true) && (


                          <td>
                            {c.created_at
                              ? new Date(c.created_at).toLocaleString()
                              : t("n_a")}
                          </td>


                          )}

                          <td className="contactsStickyActions">
                            <div className="d-flex align-items-center justify-content-end gap-2">
                              <button
                                data-academy="contact-details"
                                data-academy-contact-name={c.full_name || ""}
                                className="btn btn-warning btn-sm px-3"
                                style={{ whiteSpace: "nowrap" }}
                                onClick={() =>
                                  navigate(
                                    `/menu/sales/contacts/${c.id}/personal-details`
                                  )
                                }
                              >
                                {t("details")}
                              </button>

                              <button
                                type="button"
                                className="btn btn-outline-info btn-sm px-3"
                                style={{ whiteSpace: "nowrap" }}
                                onClick={() => openMarketingModal(c)}
                              >
                                {t("marketing")}
                              </button>

                              <div
                                className="d-flex align-items-center gap-2 px-2 py-1 rounded"
                                style={{
                                  background: "rgba(255,255,255,0.04)",
                                  border: "1px solid rgba(255,255,255,0.08)",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm px-2"
                                  title={`${t("leads")}: ${leadsTotal || 0}`}
                                  onClick={() =>
                                    navigate(
                                      `/menu/sales/leads?contact_id=${encodeURIComponent(c.id)}`
                                    )
                                  }
                                  disabled={leadsTotal === 0}
                                >
                                  L: {leadsTotal}
                                </button>

                                <span
                                  className="badge rounded-pill bg-info text-dark"
                                  title={`${t("active")}: ${leadsActive || 0}`}
                                  style={{ padding: "0.45rem 0.6rem" }}
                                >
                                  A: {leadsActive}
                                </span>

                                {getUserPermissions()?.includes(
                                  "leads.create"
                                ) && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm px-2"
                                    title={t("create_lead")}
                                    data-academy="contact-create-lead"
                                    data-academy-entity-title={c.full_name || ""}
                                    onClick={() => openLeadModal(c)}
                                  >
                                    +
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="contactsTableFooter">
                <div className="contactsTableShowing">{showingText}</div>

                <nav aria-label={t("page_navigation")}>
                  <ul className="pagination pagination-sm justify-content-end mb-0 contactsCompactPagination">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}><button className="page-link" title={t("first", { defaultValue: "First" })} aria-label={t("first", { defaultValue: "First" })} onClick={() => paginate(1)}>«</button></li>
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => paginate(currentPage - 1)}
                      >
                        {t("previous")}
                      </button>
                    </li>

                    {getCompactPaginationItems(currentPage, totalPages).map((item) => {
                      if (typeof item === "string") {
                        return (
                          <li key={item} className="page-item disabled contactsPaginationEllipsis">
                            <span className="page-link">…</span>
                          </li>
                        );
                      }
                      return (
                        <li key={item} className={`page-item ${currentPage === item ? "active" : ""}`}>
                          <button className="page-link" onClick={() => paginate(item)}>
                            {item}
                          </button>
                        </li>
                      );
                    })}

                    <li
                      className={`page-item ${
                        currentPage === totalPages || totalContacts === 0
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => paginate(currentPage + 1)}
                      >
                        {t("next")}
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages || totalContacts === 0 ? "disabled" : ""}`}><button className="page-link" title={t("last", { defaultValue: "Last" })} aria-label={t("last", { defaultValue: "Last" })} onClick={() => paginate(totalPages)}>»</button></li>
                  </ul>
                </nav>
              </div>
            </div>
          </>
        ) : totalContacts === 0 && !searchQuery.trim() ? (
          <section
            data-academy="contacts-empty-state"
            className="contactsFirstEmptyState"
            aria-labelledby="contacts-first-empty-title"
          >
            <div className="contactsFirstEmptyState__intro">
              <div
                className="contactsFirstEmptyState__mainIcon"
                aria-hidden="true"
              >
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
<path d="M11.9999 4.5C10.2039 4.5 8.74792 5.95595 8.74792 7.75195C8.74792 9.54796 10.2039 11.0039 11.9999 11.0039C13.7959 11.0039 15.2518 9.54796 15.2518 7.75195C15.2518 5.95595 13.7959 4.5 11.9999 4.5Z" fill="#fff"></path>
<path d="M11.142 12.3594C8.3917 12.3594 6.16211 14.589 6.16211 17.3393C6.16211 18.5322 7.12916 19.4993 8.32209 19.4993H15.6777C16.8706 19.4993 17.8377 18.5322 17.8377 17.3393C17.8377 14.589 15.6081 12.3594 12.8577 12.3594H11.142Z" fill="#fff"></path>
<path d="M7.24792 7.75195C7.24792 7.44767 7.27652 7.15007 7.33118 6.86169C7.09736 6.7891 6.8488 6.75 6.5911 6.75C5.21432 6.75 4.09822 7.8661 4.09822 9.24289C4.09822 10.6197 5.21432 11.7358 6.5911 11.7358C7.32812 11.7358 7.99044 11.4159 8.44683 10.9075C8.07023 10.4838 7.76829 9.9922 7.56162 9.45345C7.45276 9.16967 7.37033 8.8728 7.31735 8.56584C7.27171 8.30139 7.24792 8.02945 7.24792 7.75195Z" fill="#fff"></path>
<path d="M2 16.2327C2 14.1313 3.70354 12.4277 5.80497 12.4277H6.91506C6.413 12.8602 5.97798 13.3683 5.62769 13.9345C5.15472 14.6988 4.83624 15.5689 4.71583 16.501C4.68039 16.7754 4.66211 17.0552 4.66211 17.3393C4.66211 17.5652 4.68258 17.7864 4.72177 18.001H3.76832C2.7917 18.001 2 17.2093 2 16.2327Z" fill="#fff"></path>
<path d="M20.2314 18.001H19.278C19.3172 17.7864 19.3377 17.5652 19.3377 17.3393C19.3377 17.0552 19.3194 16.7754 19.2839 16.501C19.1635 15.5689 18.8451 14.6988 18.3721 13.9345C18.0218 13.3683 17.5868 12.8602 17.0847 12.4277H18.1948C20.2962 12.4277 21.9998 14.1313 21.9998 16.2327C21.9998 17.2093 21.2081 18.001 20.2314 18.001Z" fill="#fff"></path>
<path d="M15.557 10.9029C16.0127 11.4098 16.6735 11.7285 17.4087 11.7285C18.7835 11.7285 19.898 10.614 19.898 9.23926C19.898 7.86448 18.7835 6.75 17.4087 6.75C17.151 6.75 16.9024 6.78916 16.6686 6.86187C16.7232 7.15019 16.7518 7.44773 16.7518 7.75195C16.7518 8.0302 16.7279 8.30287 16.682 8.568C16.6295 8.87145 16.5482 9.16504 16.441 9.44585C16.235 9.98564 15.9335 10.4782 15.557 10.9029Z" fill="#fff"></path>
</svg>
              </div>

              <div>
                <h2 id="contacts-first-empty-title">
                  {t("contacts_first_empty_title", {
                    defaultValue: "Ваш список контактов пока пуст",
                  })}
                </h2>
                <p>
                  {t("contacts_first_empty_text", {
                    defaultValue:
                      "Создайте первый контакт вручную или импортируйте готовый список из CSV.",
                  })}
                </p>
              </div>
            </div>

            <div className="contactsFirstEmptyState__actions">
              {getUserPermissions()?.includes("contacts.create") && (
                <button
                  data-academy="contacts-create"
                  type="button"
                  className="contactsFirstEmptyAction"
                  onClick={openCreateModal}
                >
                  <span
                    className="contactsFirstEmptyAction__icon"
                    aria-hidden="true"
                  >
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
<path d="M7.65625 6.34459C7.65625 3.94514 9.60139 2 12.0008 2C14.4003 2 16.3454 3.94514 16.3454 6.34459C16.3454 8.74404 14.4003 10.6892 12.0008 10.6892C9.60139 10.6892 7.65625 8.74404 7.65625 6.34459Z" fill="#fff"></path>
<path d="M3.68359 17.439C3.68359 14.5395 6.0341 12.189 8.93359 12.189H15.0686C17.9681 12.189 20.3186 14.5395 20.3186 17.439V18.75C20.3186 19.9926 19.3113 21 18.0686 21H5.93359C4.69095 21 3.68359 19.9926 3.68359 18.75V17.439Z" fill="#fff"></path>
</svg>
                  </span>

                  <span className="contactsFirstEmptyAction__content">
                    <strong>
                      {t("contacts_first_empty_create", {
                        defaultValue: "Создать контакт",
                      })}
                    </strong>
                    <small>
                      {t("contacts_first_empty_create_hint", {
                        defaultValue: "Добавить клиента вручную",
                      })}
                    </small>
                  </span>

                  <span
                    className="contactsFirstEmptyAction__arrow"
                    aria-hidden="true"
                  >
                    →
                  </span>
                </button>
              )}

              {getUserPermissions()?.includes("contacts.import") && (
                <button
                  type="button"
                  className="contactsFirstEmptyAction"
                  onClick={() => navigate("/menu/sales/contacts/import")}
                >
                  <span
                    className="contactsFirstEmptyAction__icon"
                    aria-hidden="true"
                  >
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M17.25 22C18.4926 22 19.5 20.9926 19.5 19.75V19.0058H20.5C21.3284 19.0058 22 18.3342 22 17.5058V13.25C22 12.4216 21.3284 11.75 20.5 11.75H10.5C9.67157 11.75 9 12.4216 9 13.25V17.5058C9 18.3342 9.67157 19.0058 10.5 19.0058H18V19.75C18 20.1642 17.6642 20.5 17.25 20.5H6.75C6.33579 20.5 6 20.1642 6 19.75V9.75H10.0009C11.2442 9.75 12.2518 8.74166 12.2509 7.4984L12.248 3.5H17.25C17.6642 3.5 18 3.83579 18 4.25V11.7383H19.5V4.25C19.5 3.00736 18.4926 2 17.25 2H12.1177C11.5207 2 10.9482 2.23725 10.5262 2.65951L5.15851 8.03055C4.73686 8.45247 4.5 9.02455 4.5 9.62105V19.75C4.5 20.9926 5.50736 22 6.75 22H17.25ZM10.7488 4.55876L7.05986 8.25H10.0009C10.4153 8.25 10.7512 7.91389 10.7509 7.49947L10.7488 4.55876ZM18.5578 17.4923C18.5336 17.4923 18.5115 17.4863 18.4913 17.4742C18.4711 17.4581 18.4591 17.4379 18.455 17.4137L17.2639 13.3626C17.2598 13.3545 17.2578 13.3444 17.2578 13.3323C17.2578 13.284 17.286 13.2598 17.3425 13.2598H18.3764C18.4288 13.2598 18.4611 13.286 18.4732 13.3384L19.0778 15.89C19.0818 15.9061 19.0879 15.9142 19.096 15.9142C19.104 15.9142 19.1101 15.9061 19.1141 15.89L19.7006 13.3384C19.7127 13.286 19.7449 13.2598 19.7973 13.2598H20.8071C20.8394 13.2598 20.8615 13.2698 20.8736 13.29C20.8898 13.3061 20.8938 13.3303 20.8857 13.3626L19.6825 17.4137C19.6784 17.4379 19.6663 17.4581 19.6462 17.4742C19.626 17.4863 19.6039 17.4923 19.5797 17.4923H18.5578ZM14.6126 17.377C14.8665 17.4858 15.1628 17.5402 15.5014 17.5402C15.8279 17.5402 16.1141 17.4878 16.36 17.383C16.6059 17.2782 16.7974 17.1291 16.9345 16.9356C17.0715 16.7381 17.14 16.5043 17.14 16.2342C17.14 15.9561 17.0554 15.7202 16.8861 15.5268C16.7168 15.3292 16.5052 15.1942 16.2512 15.1216C16.0013 15.045 15.8602 15.0007 15.8279 14.9886C15.5941 14.9161 15.4208 14.8576 15.3079 14.8133C15.1951 14.7649 15.1104 14.7165 15.054 14.6681C15.0016 14.6157 14.9754 14.5533 14.9754 14.4807C14.9754 14.388 15.0117 14.3154 15.0842 14.263C15.1608 14.2106 15.2636 14.1844 15.3926 14.1844C15.578 14.1844 15.7252 14.2348 15.834 14.3356C15.9428 14.4323 15.9972 14.5412 15.9972 14.6621V14.6681C15.9972 14.6964 16.0053 14.7185 16.0214 14.7347C16.0376 14.7508 16.0597 14.7588 16.0879 14.7588H17.0252C17.0534 14.7588 17.0755 14.7508 17.0917 14.7347C17.1078 14.7185 17.1158 14.6964 17.1158 14.6681V14.6137C17.1158 14.3396 17.0413 14.0957 16.8921 13.8821C16.747 13.6685 16.5455 13.5032 16.2875 13.3863C16.0335 13.2694 15.7493 13.2109 15.4349 13.2109C15.1366 13.2109 14.8665 13.2633 14.6247 13.3681C14.3869 13.473 14.1994 13.6241 14.0624 13.8216C13.9253 14.0151 13.8568 14.2388 13.8568 14.4928C13.8568 14.8757 13.9918 15.168 14.2619 15.3695C14.532 15.5711 14.8665 15.7323 15.2656 15.8533L15.441 15.9016C15.6546 15.9661 15.8017 16.0205 15.8824 16.0649C15.963 16.1092 16.0033 16.1757 16.0033 16.2644C16.0033 16.341 15.971 16.4116 15.9065 16.4761C15.8421 16.5365 15.7352 16.5668 15.5861 16.5668C15.3886 16.5668 15.2293 16.5244 15.1084 16.4398C14.9915 16.3511 14.9331 16.2503 14.9331 16.1374V16.1012C14.9331 16.073 14.925 16.0508 14.9089 16.0347C14.8927 16.0185 14.8706 16.0105 14.8424 16.0105H13.9052C13.8769 16.0105 13.8548 16.0185 13.8386 16.0347C13.8225 16.0508 13.8145 16.073 13.8145 16.1012V16.1858C13.8145 16.4559 13.883 16.6937 14.02 16.8993C14.1611 17.1049 14.3586 17.2641 14.6126 17.377ZM11.829 17.5402C11.4985 17.5402 11.2062 17.4757 10.9522 17.3468C10.7023 17.2178 10.5068 17.0364 10.3657 16.8026C10.2287 16.5647 10.1602 16.2906 10.1602 15.9802V14.7588C10.1602 14.4525 10.2287 14.1824 10.3657 13.9486C10.5068 13.7148 10.7023 13.5334 10.9522 13.4044C11.2062 13.2754 11.4985 13.2109 11.829 13.2109C12.1595 13.2109 12.4498 13.2714 12.6997 13.3923C12.9536 13.5133 13.1491 13.6846 13.2862 13.9063C13.4273 14.124 13.4978 14.3779 13.4978 14.6681C13.4978 14.7044 13.4676 14.7266 13.4071 14.7347L12.4518 14.7951H12.4336C12.3853 14.7951 12.3611 14.773 12.3611 14.7286C12.3611 14.5633 12.3127 14.4323 12.216 14.3356C12.1192 14.2348 11.9902 14.1844 11.829 14.1844C11.6678 14.1844 11.5388 14.2348 11.442 14.3356C11.3453 14.4323 11.2969 14.5633 11.2969 14.7286V16.0286C11.2969 16.1899 11.3453 16.3209 11.442 16.4216C11.5388 16.5184 11.6678 16.5668 11.829 16.5668C11.9902 16.5668 12.1192 16.5184 12.216 16.4216C12.3127 16.3209 12.3611 16.1899 12.3611 16.0286C12.3611 16.0044 12.3691 15.9863 12.3853 15.9742C12.4014 15.9621 12.4236 15.9581 12.4518 15.9621L13.4071 16.0105C13.4676 16.0145 13.4978 16.0347 13.4978 16.0709C13.4978 16.3612 13.4273 16.6171 13.2862 16.8388C13.1491 17.0605 12.9536 17.2339 12.6997 17.3588C12.4498 17.4798 12.1595 17.5402 11.829 17.5402Z" fill="#fff"></path>
</svg>
                  </span>

                  <span className="contactsFirstEmptyAction__content">
                    <strong>
                      {t("contacts_first_empty_import", {
                        defaultValue: "Импортировать CSV",
                      })}
                    </strong>
                    <small>
                      {t("contacts_first_empty_import_hint", {
                        defaultValue: "Загрузить готовый список контактов",
                      })}
                    </small>
                  </span>

                  <span
                    className="contactsFirstEmptyAction__arrow"
                    aria-hidden="true"
                  >
                    →
                  </span>
                </button>
              )}
            </div>
          </section>
        ) : (
          <p className="text-warning">{t("sales_contacts_empty")}</p>
        )}
      </div>

      {marketingModalOpen && marketingContact && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("contacts_marketing_label")} {marketingContact.full_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeMarketingModal}
                ></button>
              </div>
              <div className="modal-body">
                <MarketingContactPanel
                  contactId={marketingContact.id}
                  canEdit={canUpdateContacts}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeMarketingModal}
                >
                  {t("close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {leadModalOpen && leadModalContact && (
        <div
          className="modal fade show"
          data-academy="contact-lead-modal"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("create_lead_for") + ": " + leadModalContact.full_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeLeadModal}
                ></button>
              </div>

              <div className="modal-body">
                {leadError && (
                  <div className="alert alert-danger py-1 mb-2">
                    {leadError}
                  </div>
                )}

                {sourcesError && (
                  <div className="alert alert-warning py-1 mb-2">
                    {sourcesError}
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">
                    {t("lead_title")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    data-academy="contact-lead-title"
                    value={leadForm.title}
                    onChange={(e) => handleLeadChange("title", e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    {t("lead_source")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={leadForm.source_id || ""}
                    onChange={(e) =>
                      handleLeadChange("source_id", e.target.value)
                    }
                    disabled={sourcesLoading}
                    required
                  >
                    <option value="">
                      {sourcesLoading
                        ? `${t("loading")}...`
                        : t("select_source")}
                    </option>
                    {sources.map((s) => (
                      <option key={s.id} value={s.id}>
                        {t(s.name)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">{t("lead_budget")}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={leadForm.budget}
                    onChange={(e) => handleLeadChange("budget", e.target.value)}
                    placeholder="1000"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">{t("description")}</label>
                  <textarea
                    className="form-control"
                    data-academy="contact-lead-description"
                    rows={3}
                    value={leadForm.description}
                    onChange={(e) =>
                      handleLeadChange("description", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeLeadModal}
                  disabled={leadSaving}
                >
                  {t("cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-academy="contact-lead-submit"
                  onClick={handleLeadSubmit}
                  disabled={leadSaving}
                >
                  {leadSaving ? t("saving") : t("create_lead")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {detailsModalOpen && detailsContact && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {detailsModalType === "email"
                    ? `${t("edit_emails")}: ${detailsContact.full_name}`
                    : `${t("edit_phones")}: ${detailsContact.full_name}`}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDetailsModal}
                ></button>
              </div>

              <div className="modal-body">
                {detailsError && (
                  <div className="alert alert-danger py-1 mb-2">
                    {detailsError}
                  </div>
                )}

                {detailsItems.length === 0 && (
                  <p className="text-muted">
                    {detailsModalType === "email"
                      ? t("no_emails")
                      : t("no_phones")}
                  </p>
                )}

                {detailsItems.map((item, index) => (
                  <div
                    key={item.id ?? index}
                    className="d-flex align-items-center mb-2 gap-2"
                  >
                    <input
                      type={detailsModalType === "email" ? "email" : "text"}
                      className="form-control"
                      placeholder={
                        detailsModalType === "email"
                          ? t("email_input_placeholder")
                          : t("phone_input_placeholder")
                      }
                      value={
                        detailsModalType === "email" ? item.email : item.phone
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        setDetailsItems((prev) =>
                          prev.map((it, idx) =>
                            idx === index
                              ? {
                                  ...it,
                                  [detailsModalType === "email"
                                    ? "email"
                                    : "phone"]: value,
                                }
                              : it
                          )
                        );
                      }}
                    />

                    <select
                      className="form-select"
                      style={{ maxWidth: "140px" }}
                      value={item.status || "main"}
                      onChange={(e) => {
                        const value = e.target.value;

                        setDetailsItems((prev) => {
                          if (value === "main") {
                            return prev.map((it, idx) =>
                              idx === index
                                ? { ...it, status: "main" }
                                : {
                                    ...it,
                                    status:
                                      it.status === "main"
                                        ? "corporate"
                                        : it.status,
                                  }
                            );
                          }

                          return prev.map((it, idx) =>
                            idx === index ? { ...it, status: value } : it
                          );
                        });
                      }}
                    >
                      <option value="main">{t("status_main")}</option>
                      <option value="corporate">{t("status_corporate")}</option>
                      <option value="personal">{t("status_personal")}</option>
                    </select>

                    {getUserPermissions()?.includes("contacts.update") && (
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() =>
                          setDetailsItems((prev) =>
                            prev.filter((_, idx) => idx !== index)
                          )
                        }
                      >
                        {t("delete")}
                      </button>
                    )}
                  </div>
                ))}

                {getUserPermissions()?.includes("contacts.update") && (
                  <button
                    type="button"
                    className="btn btn-outline-primary mt-2"
                    onClick={() =>
                      setDetailsItems((prev) => [
                        ...prev,
                        detailsModalType === "email"
                          ? { id: null, email: "", status: "personal" }
                          : { id: null, phone: "", status: "personal" },
                      ])
                    }
                  >
                    {detailsModalType === "email"
                      ? t("add_email")
                      : t("add_phone")}
                  </button>
                )}

                <small className="text-muted d-block mt-2">
                  {detailsModalType === "email"
                    ? t("email_format_hint")
                    : t("phone_format_hint")}
                </small>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeDetailsModal}
                  disabled={detailsSaving}
                >
                  {t("cancel")}
                </button>

                {getUserPermissions()?.includes("contacts.update") && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      if (detailsModalType === "email") {
                        const invalid = detailsItems.filter(
                          (i) =>
                            !i.email ||
                            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(i.email.trim())
                        );
                        if (invalid.length) {
                          setDetailsError(t("invalid_email"));
                          return;
                        }
                      } else {
                        const invalid = detailsItems.filter(
                          (i) =>
                            !i.phone ||
                            !/^\+?[0-9\s\-()]{6,20}$/.test(i.phone.trim())
                        );
                        if (invalid.length) {
                          setDetailsError(t("invalid_phone"));
                          return;
                        }
                      }

                      const mainCount = detailsItems.filter(
                        (i) => i.status === "main"
                      ).length;

                      if (mainCount > 1) {
                        setDetailsError(
                          detailsModalType === "email"
                            ? t("only_one_main_email")
                            : t("only_one_main_phone")
                        );
                        return;
                      }

                      try {
                        setDetailsSaving(true);
                        setDetailsError("");

                        const payload = {
                          full_name: detailsContact.full_name,
                          company_id: detailsContact.company_id,
                          owner_id: detailsContact.owner_id,
                          custom_field_values:
                            detailsContact.custom_field_values || {},
                          emails:
                            detailsModalType === "email"
                              ? detailsItems
                              : detailsContact.emails,
                          phones:
                            detailsModalType === "phone"
                              ? detailsItems
                              : detailsContact.phones,
                        };

                        await api.put(`/contacts/${detailsContact.id}`, payload);

                        await fetchContacts(
                          currentPage,
                          contactsPerPage,
                          searchQuery
                        );
                        closeDetailsModal();
                      } catch (e) {
                        console.error(e);
                        setDetailsError(
                          e.response?.data?.detail || t("error_saving_changes")
                        );
                      } finally {
                        setDetailsSaving(false);
                      }
                    }}
                    disabled={detailsSaving}
                  >
                    {detailsSaving ? t("saving") : t("save_changes")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {createModalOpen && (
        <div
          data-academy="contact-create-modal"
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("create_contact")}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeCreateModal}
                ></button>
              </div>

              <div className="modal-body">
                {createError && (
                  <div className="alert alert-danger py-1 mb-2">
                    {createError}
                  </div>
                )}

                {companiesError && (
                  <div className="alert alert-warning py-1 mb-2">
                    {companiesError}
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">
                    {t("sales_contacts_name")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={createForm.full_name}
                    onChange={(e) =>
                      handleCreateChange("full_name", e.target.value)
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    {t("sales_contacts_email")}
                  </label>
                  <input
                    type="email"
                    className={
                      "form-control" + (createEmailError ? " is-invalid" : "")
                    }
                    value={createForm.email}
                    onChange={(e) => handleCreateChange("email", e.target.value)}
                    placeholder={t("email_input_placeholder")}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    {t("sales_contacts_phone")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={createForm.phone}
                    onChange={(e) => handleCreateChange("phone", e.target.value)}
                    placeholder={t("phone_input_placeholder")}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    {t("sales_contacts_company")}
                  </label>
                  <select
                    className="form-select"
                    value={createForm.company_id || ""}
                    onChange={(e) =>
                      handleCreateChange("company_id", e.target.value)
                    }
                    disabled={companiesLoading}
                  >
                    <option value="">
                      {companiesLoading
                        ? `${t("loading")}...`
                        : t("select_company")}
                    </option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.firm_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    {t("sales_contacts_owner")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={createForm.owner_id}
                    onChange={(e) =>
                      handleCreateChange("owner_id", e.target.value)
                    }
                    placeholder={t("owner_default_current")}
                  />
                  <small className="text-muted">{t("owner_default_hint")}</small>
                </div>

                {customFields.length > 0 && (
                  <section className="contactCreatePresetBlock">
                    <div className="contactCreatePresetHead">
                      <div>
                        <span className="contactCreatePresetEyebrow">
                          {t("business_preset_fields") || "Business preset fields"}
                        </span>
                        <h6>{t("custom_fields") || "Custom fields"}</h6>
                        <p>
                          {t("business_preset_fields_hint") ||
                            "These fields are configured automatically for this tenant business type."}
                        </p>
                      </div>

                      <span className="contactCreatePresetCount">
                        {customFields.length}
                      </span>
                    </div>

                    <div className="contactCreatePresetGrid">
                      {customFields.map((field) => {
                        const value =
                          createForm.custom_field_values?.[field.key] ?? "";

                        const fieldOptions = Array.isArray(field.options_json)
                          ? field.options_json
                          : field.options_json?.options || [];

                        return (
                          <div className="contactCreatePresetField" key={field.id}>
                            <label className="form-label">
                              <span>
                                {t(field.label || field.key)}
                                {field.is_required ? " *" : ""}
                              </span>
                              <small>{field.key}</small>
                            </label>

                            {field.data_type === "boolean" ? (
                              <select
                                className="form-select"
                                value={value}
                                onChange={(e) =>
                                  handleCreateCustomFieldChange(
                                    field.key,
                                    e.target.value
                                  )
                                }
                              >
                                <option value="">{t("n_a")}</option>
                                <option value="true">{t("yes")}</option>
                                <option value="false">{t("no")}</option>
                              </select>
                            ) : field.data_type === "select" ? (
                              <select
                                className="form-select"
                                value={value}
                                onChange={(e) =>
                                  handleCreateCustomFieldChange(
                                    field.key,
                                    e.target.value
                                  )
                                }
                              >
                                <option value="">{t("n_a")}</option>
                                {fieldOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={
                                  field.data_type === "number"
                                    ? "number"
                                    : field.data_type === "date"
                                      ? "date"
                                      : "text"
                                }
                                className="form-control"
                                value={value}
                                onChange={(e) =>
                                  handleCreateCustomFieldChange(
                                    field.key,
                                    e.target.value
                                  )
                                }
                                placeholder={
                                  field.default_value
                                    ? String(field.default_value)
                                    : field.label || field.key
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeCreateModal}
                  disabled={createSaving}
                >
                  {t("cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateSubmit}
                  disabled={createSaving}
                >
                  {createSaving ? t("saving") : t("create")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {bulkMarketingModalOpen && (
        <div
          className="modal fade show contactsMarketingSendModal"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">{t("contacts_send_campaign")}</h5>
                  <small className="text-muted">
                    {t("contacts_selected_clients")} {selectedIds.length}
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeBulkMarketingModal}
                  disabled={bulkMarketingSending}
                ></button>
              </div>

              <div className="modal-body contactsMarketingSendBody">
                {bulkMarketingError && (
                  <div className="alert alert-danger py-2">{bulkMarketingError}</div>
                )}

                <div className="contactsMarketingStep">
                  <div className="contactsMarketingStepNumber">01</div>
                  <div className="contactsMarketingStepContent">
                    <h6>{t("contacts_choose_campaign")}</h6>
                    <p>{t("contacts_campaign_selection_hint")}</p>

                    <input
                      type="text"
                      className="form-control contactsMarketingSearch"
                      value={bulkMarketingCampaignSearch}
                      onChange={(e) => setBulkMarketingCampaignSearch(e.target.value)}
                      placeholder={t("contacts_search_campaign")}
                    />

                    <div className="contactsMarketingCampaignList">
                      {bulkMarketingLoading ? (
                        <div className="contactsMarketingEmpty">{t("contacts_loading_campaigns")}</div>
                      ) : filteredBulkMarketingCampaigns.length === 0 ? (
                        <div className="contactsMarketingEmpty">
                          {t("contacts_no_campaigns")}
                        </div>
                      ) : (
                        filteredBulkMarketingCampaigns.map((campaign) => (
                          <button
                            key={campaign.id}
                            type="button"
                            className={`contactsMarketingCampaignOption ${
                              String(bulkMarketingForm.campaign_id) === String(campaign.id)
                                ? "active"
                                : ""
                            }`}
                            onClick={() =>
                              handleBulkMarketingFormChange("campaign_id", String(campaign.id))
                            }
                          >
                            <span>
                              <strong>{campaign.title || `Campaign #${campaign.id}`}</strong>
                              <small>
                                {campaign.delivery_channel || "channel"} · {campaign.status || "draft"}
                              </small>
                            </span>
                            <i>{String(bulkMarketingForm.campaign_id) === String(campaign.id) ? "✓" : ""}</i>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="contactsMarketingStep">
                  <div className="contactsMarketingStepNumber">02</div>
                  <div className="contactsMarketingStepContent">
                    <h6>{t("contacts_when_to_send")}</h6>
                    <div className="contactsMarketingModeGrid">
                      <label className="contactsMarketingMode">
                        <input
                          type="radio"
                          name="bulkMarketingSendMode"
                          checked={bulkMarketingForm.send_mode === "now"}
                          onChange={() => handleBulkMarketingFormChange("send_mode", "now")}
                        />
                        <span>
                          <strong>{t("contacts_send_now")}</strong>
                          <small>{t("contacts_send_now_hint")}</small>
                        </span>
                      </label>

                      <label className="contactsMarketingMode">
                        <input
                          type="radio"
                          name="bulkMarketingSendMode"
                          checked={bulkMarketingForm.send_mode === "scheduled"}
                          onChange={() => handleBulkMarketingFormChange("send_mode", "scheduled")}
                        />
                        <span>
                          <strong>{t("contacts_scheduled")}</strong>
                          <small>{t("contacts_scheduled_hint")}</small>
                        </span>
                      </label>
                    </div>

                    {bulkMarketingForm.send_mode === "scheduled" && (
                      <input
                        type="datetime-local"
                        className="form-control mt-2"
                        value={bulkMarketingForm.scheduled_at}
                        onChange={(e) => handleBulkMarketingFormChange("scheduled_at", e.target.value)}
                      />
                    )}
                  </div>
                </div>

                {selectedBulkMarketingCampaign && (
                  <div className="contactsMarketingSummary">
                    <strong>{selectedBulkMarketingCampaign.title}</strong>
                    <span>
                      {t("contacts_clients_attached")} {selectedIds.length}{t("contacts_campaign_channel_hint")}
                    </span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeBulkMarketingModal}
                  disabled={bulkMarketingSending}
                >
                  {t("cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleBulkMarketingSubmit}
                  disabled={bulkMarketingSending}
                >
                  {bulkMarketingSending ? t("sending") : t("contacts_send_campaign")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BulkEmailModal
        open={bulkEmailModalOpen}
        onClose={closeBulkEmailModal}
        form={bulkEmailForm}
        errors={bulkEmailErrors}
        sending={bulkEmailSending}
        recipients={bulkRecipients}
        onRecipientChange={handleBulkRecipientEmailChange}
        onChange={handleBulkEmailChange}
        onSubmit={handleBulkEmailSubmit}
      />

      {sendEmailModalOpen && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header d-flex align-items-center justify-content-between">
                <div className="d-flex flex-column">
                  <h5 className="modal-title mb-0">
                    {t("send_email_to") +
                      ": " +
                      (sendEmailContact?.full_name || "")}
                  </h5>
                  <small className="text-muted">
                    {sendEmailForm.to ? sendEmailForm.to : t("email_to_hint")}
                  </small>
                </div>

                <div className="d-flex align-items-center gap-3">
                  {allowSenderOverrideSingle && (
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted small">
                        {t("email_sender")}
                      </span>

                      <select
                        className="form-select form-select-sm"
                        value={sendEmailForm?.sender || "auto"}
                        onChange={(e) =>
                          handleSendEmailChange("sender", e.target.value)
                        }
                        disabled={sendEmailSending}
                        style={{
                          width: 240,
                          backgroundColor: "rgba(0,0,0,0.25)",
                          color: "rgba(255,255,255,0.92)",
                          border: "1px solid rgba(255,255,255,0.12)",
                        }}
                      >
                        <option value="auto">{t("sender_auto")}</option>
                        <option value="company">{t("sender_company")}</option>
                        <option value="employee">{t("sender_employee")}</option>
                      </select>
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeSendEmailModal}
                  ></button>
                </div>
              </div>

              <div className="modal-body">
                <div
                  className="mb-3 p-3 rounded"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="small text-muted">{t("ai_draft_hint")}</div>

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleGenerateAiDraft}
                      disabled={sendEmailDraftLoading || !sendEmailContact?.id}
                    >
                      {sendEmailDraftLoading
                        ? t("generating")
                        : t("generate_with_ai")}
                    </button>
                  </div>

                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small text-muted mb-1">
                        {t("tone")}
                      </label>
                      <select
                        className="form-select form-select-sm"
                        value={sendEmailAiTone}
                        onChange={(e) => setSendEmailAiTone(e.target.value)}
                        disabled={sendEmailDraftLoading}
                      >
                        <option value="professional">
                          {t("tone_professional")}
                        </option>
                        <option value="friendly">{t("tone_friendly")}</option>
                        <option value="short">{t("tone_short")}</option>
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small text-muted mb-1">
                        {t("objective")}
                      </label>
                      <select
                        className="form-select form-select-sm"
                        value={sendEmailAiObjective}
                        onChange={(e) => setSendEmailAiObjective(e.target.value)}
                        disabled={sendEmailDraftLoading}
                      >
                        <option value="follow_up">
                          {t("objective_follow_up")}
                        </option>
                        <option value="meeting">{t("objective_meeting")}</option>
                        <option value="proposal">
                          {t("objective_proposal")}
                        </option>
                        <option value="reminder">
                          {t("objective_reminder")}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <HtmlEditorWithPreview
                  mode="single"
                  form={sendEmailForm}
                  errors={sendEmailErrors}
                  sending={sendEmailSending}
                  onChange={handleSendEmailChange}
                  onSubmit={handleSendEmailSubmit}
                  onCancel={closeSendEmailModal}
                  hideSenderInAdvanced={allowSenderOverrideSingle}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <TelephonyModal
        open={callModalOpen}
        onClose={closeCallModal}
        contact={callContact}
        phone={callPhone}
        setPhone={setCallPhone}
        error={callError}
        isCalling={isCalling}
        activeCallSid={activeCallSid}
        isMuted={isMuted}
        isOnHold={isOnHold}
        isRecording={isRecording}
        onStartCall={handleStartCall}
        onHangup={handleHangupCall}
        onToggleMute={handleToggleMute}
        onToggleHold={handleToggleHold}
        onToggleRecord={handleToggleRecord}
        micLevel={micLevel}
      />
    </div>
  );
}

export default ContactsPage;
